import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { bulkAddSessionsSchema, BulkAddSessionsFormValues } from "./schemas/bulkAddSessionsSchema";
import { CalendarIcon } from "lucide-react";
import { format, isValid, startOfToday, isAfter, addYears, compareAsc } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CampLocation } from "@/types/campLocation";
import { generateSessionDates, formatRecurrencePattern } from "./utils/sessionGenerator";
import { utcEpochDayKey, utcEpochDayKeyNumber } from "@/utils/dateEpochKey";
import { Label } from "@/components/ui/label";
import { SortableOption } from "@/types/fieldsOptions";
import { StatusSection } from "./form-sections/StatusSection";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addMonths, subMonths } from "date-fns";
import { Variation } from "@/components/admin/locations/types/variation";
import { useIncrementOptions } from "@/components/admin/locations/hooks/useIncrementOptions";
import { getDecimalEnums } from "@/services/decimalEnumService";
import { db } from "@/services/firebaseService";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  enumerateBulkPreviewRows,
  applyBulkPreviewExclusions,
  resolveBulkPreviewRowLabels,
  type BulkPreviewExtras,
  type BulkPreviewLabelContext,
} from "./bulkSessionPreview";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { RegistrationEventsSection } from "@/components/admin/locations/components/calendar/dialogs/components/form-sections/RegistrationEventsSection";
import { parseRegEventDateTime } from "./utils/parseRegEventDateTime";
import { doc, getDoc } from "firebase/firestore";
import { sessionLinkDefaults } from "@/functions-shared/offer/sessionLinkDefaults";
import { applyProgramToSessionForm } from "./utils/applyProgramToSessionForm";
import { ProgramWebsiteReadOnly } from "@/components/admin/locations/components/ProgramLinkReadOnly";
import {
  AVAILABILITY_STATUS_UNKNOWN,
  SIGNUP_STATUS_UNKNOWN,
  isAvailabilityStatus,
  isSignupStatus,
} from "@/functions-shared/offer/offerStatusEnums";

/**
 * RowFormat multiline assembly appends each physical line to a buffer until the RowFormat regex
 * matches. If the pattern never matches (wrong paste, partial data), the buffer grows without bound
 * and `.test()` runs on the entire buffer every line → O(n²) work and browser freeze.
 */
const BULK_DATE_LIST_MAX_PHYS_LINES_PER_LOGICAL_ROW = 520;
const BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS = 600_000;
/** Hard cap for synchronous parse — beyond this we bail out rather than locking the main thread. */
const BULK_DATE_LIST_MAX_INPUT_CHARS = 2_200_000;

/** Verbose bulk-modal logs (console). Off by default; set `localStorage.setItem('kidsbooked:bulkModalDebug','1')` to enable. */
function isBulkModalDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage?.getItem("kidsbooked:bulkModalDebug") === "1";
  } catch {
    return false;
  }
}

function bulkModalDevLog(...args: unknown[]): void {
  if (!isBulkModalDebugEnabled()) return;
  try {
    // eslint-disable-next-line no-console
    console.log(...args);
  } catch {
    /* ignore */
  }
}

const PROG_DEFAULT_SENTINEL = "<progDefault>";
function isProgDefaultSentinel(raw: unknown): boolean {
  return String(raw ?? "").trim().toLowerCase() === PROG_DEFAULT_SENTINEL.toLowerCase();
}

interface BulkAddSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: CampLocation;
  onSubmit: (formValues: BulkAddSessionsFormValues, generatedDates: Date[]) => Promise<void>;
  availabilityStatusOptions: SortableOption[];
  signupStatusOptions: SortableOption[];
  /**
   * Modal mode:
   * - "location": current behavior (single-location operator flow)
   * - "org": cross-location/org bulk add flow (RowFormat required)
   */
  mode?: "location" | "org";
  /** Optional prefill for the "Enter date list" textarea (org-mode uses this heavily). */
  initialDateListInput?: string;
  /** Optional override for initial signup status selection (e.g. "Open to the general public"). */
  initialSignupStatusId?: string;
  /**
   * Optional campLocations doc id → display name map, used to label the
   * per-session preview table's Location column for cross-location org rows
   * (KB-1460). When absent, rows fall back to the base location name / the id.
   */
  locationNameById?: Record<string, string>;
}

export const BulkAddSessionsModal: React.FC<BulkAddSessionsModalProps> = ({
  isOpen,
  onClose,
  location,
  onSubmit,
  availabilityStatusOptions,
  signupStatusOptions,
  mode = "location",
  initialDateListInput,
  initialSignupStatusId,
  locationNameById,
}) => {
  const isOrgMode = mode === "org";
  const debugLog = useCallback((event: string, data?: Record<string, unknown>) => {
    if (!isBulkModalDebugEnabled()) return;
    try {
      // eslint-disable-next-line no-console
      console.log(`[BulkAddSessionsModal] ${event}`, data ?? {});
    } catch {
      // ignore
    }
  }, []);
  const debugWarn = useCallback((event: string, data?: Record<string, unknown>) => {
    if (!isBulkModalDebugEnabled()) return;
    try {
      // eslint-disable-next-line no-console
      console.warn(`[BulkAddSessionsModal] ${event}`, data ?? {});
    } catch {
      // ignore
    }
  }, []);

  const debugSummarizeDateListExtras = useCallback((extrasUnknown: unknown) => {
    // Best-effort analysis of dateListExtras to help debug zod validation failures.
    const summary = {
      dateKeys: 0,
      totalRows: 0,
      hasAnyArrayRows: false,
      // Common zod constraints in bulkAddSessionsSchema.ts
      maxDescriptionLength: 0,
      maxEligibilityNotesLength: 0,
      maxDescriptionPrefixLength: 0,
      descriptionOver2000Count: 0,
      eligibilityNotesOver2000Count: 0,
      descriptionPrefixOver2000Count: 0,
      invalidEndDateCount: 0,
      // samples to help find the exact row quickly
      samples: [] as Array<{
        dateKey: string;
        rowIndex: number;
        field?: "description" | "eligibilityNotes" | "descriptionPrefix" | "endDate";
        length?: number;
        limit?: number;
        title?: string;
        offeringId?: string;
        value?: string;
      }>,
    };

    const pushSample = (s: (typeof summary.samples)[number]) => {
      // Keep enough rows to be useful for fixing, but cap to avoid huge logs/state.
      if (summary.samples.length < 50) summary.samples.push(s);
    };

    const scanRow = (dateKey: string, row: any, rowIndex: number) => {
      summary.totalRows += 1;
      const desc = typeof row?.description === "string" ? row.description : undefined;
      const elig = typeof row?.eligibilityNotes === "string" ? row.eligibilityNotes : undefined;
      const descPrefix = typeof row?.descriptionPrefix === "string" ? row.descriptionPrefix : undefined;
      const title = typeof row?.title === "string" ? row.title : undefined;
      const offeringId = typeof row?.offeringId === "string" ? row.offeringId : undefined;

      const descLen = desc?.length ?? 0;
      const eligLen = elig?.length ?? 0;
      const descPrefixLen = descPrefix?.length ?? 0;

      if (descLen > summary.maxDescriptionLength) summary.maxDescriptionLength = descLen;
      if (eligLen > summary.maxEligibilityNotesLength) summary.maxEligibilityNotesLength = eligLen;
      if (descPrefixLen > summary.maxDescriptionPrefixLength) summary.maxDescriptionPrefixLength = descPrefixLen;

      if (descLen > 2000) {
        summary.descriptionOver2000Count += 1;
        pushSample({ dateKey, rowIndex, field: "description", length: descLen, limit: 2000, title, offeringId, value: desc });
      }
      if (eligLen > 2000) {
        summary.eligibilityNotesOver2000Count += 1;
        pushSample({ dateKey, rowIndex, field: "eligibilityNotes", length: eligLen, limit: 2000, title, offeringId, value: elig });
      }
      if (descPrefixLen > 2000) {
        summary.descriptionPrefixOver2000Count += 1;
        pushSample({ dateKey, rowIndex, field: "descriptionPrefix", length: descPrefixLen, limit: 2000, title, offeringId, value: descPrefix });
      }

      const endDate = row?.endDate;
      if (endDate instanceof Date && !isValid(endDate)) {
        summary.invalidEndDateCount += 1;
        pushSample({ dateKey, rowIndex, field: "endDate", title, offeringId });
      }
    };

    if (!extrasUnknown || typeof extrasUnknown !== "object") return summary;
    const extras = extrasUnknown as Record<string, unknown>;
    const keys = Object.keys(extras);
    summary.dateKeys = keys.length;
    for (const dateKey of keys) {
      const v = extras[dateKey] as any;
      if (Array.isArray(v)) {
        summary.hasAnyArrayRows = true;
        v.forEach((row, idx) => scanRow(dateKey, row, idx));
      } else if (v && typeof v === "object") {
        scanRow(dateKey, v, 0);
      }
    }
    return summary;
  }, []);

  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { userProfile } = useAuth();
  
  // Check if user is admin
  const isAdmin = userProfile?.role === 'admin_user';
  
  // Add another checkbox state
  const [addAnotherChecked, setAddAnotherChecked] = useState(false);
  const [savedFormData, setSavedFormData] = useState<BulkAddSessionsFormValues | null>(null);
  const processedAddAnother = useRef(false);
  
  // State for custom calendar navigation
  const [startMonth, setStartMonth] = useState(new Date());
  const [endMonth, setEndMonth] = useState(new Date());
  
  // JAIL BREAK: Local state for instant UI updates (Session Length Selector)
  const [selectedProgramLocal, setSelectedProgramLocal] = useState<string>("");
  const [showSessionLengthLocal, setShowSessionLengthLocal] = useState<boolean>(false);
  const [sessionLengthOptionsLocal, setSessionLengthOptionsLocal] = useState<any[]>([]);
  const [defaultIncrementLocal, setDefaultIncrementLocal] = useState<string>("");
  const [enumsMap, setEnumsMap] = useState<Map<string, any>>(new Map());
  
  // Lock state management for four fields
  type FieldKey = 'description' | 'eligibilityNotes' | 'url' | 'regUrl';
  type LockStatus = 'unset' | 'locked' | 'unlocked';
  const [lockStates, setLockStates] = useState<Record<FieldKey, LockStatus>>({
    description: 'unset',
    eligibilityNotes: 'unset',
    url: 'unset',
    regUrl: 'unset'
  });
  const [savedLockStates, setSavedLockStates] = useState<Record<FieldKey, LockStatus> | null>(null);
  const setFieldLockedOnUserEdit = (key: FieldKey) => {
    setLockStates(prev => (prev[key] === 'unset' ? { ...prev, [key]: 'locked' } : prev));
  };
  const toggleFieldLock = (key: FieldKey) => {
    setLockStates(prev => ({
      ...prev,
      [key]: prev[key] === 'locked' ? 'unlocked' : 'locked'
    }));
  };
  const shouldOverwriteFromProgram = (key: FieldKey) => lockStates[key] !== 'locked';
  
  // Load increment options hook
  const { incrementOptions, loading: incrementOptionsLoading } = useIncrementOptions();
  
  // Safe access to programs (variations) with fallback to empty array
  const programs: Variation[] = location?.variations || [];
  
  // No-evidence default: unknown on both axes (KB-4101).
  const defaultAvailabilityStatus = AVAILABILITY_STATUS_UNKNOWN;
  const defaultSignupStatus = SIGNUP_STATUS_UNKNOWN;
  const initialSignupStatus =
    isSignupStatus(initialSignupStatusId) && signupStatusOptions.some((o) => o.id === initialSignupStatusId)
      ? initialSignupStatusId
      : defaultSignupStatus;
  
  const form = useForm<BulkAddSessionsFormValues>({
    resolver: zodResolver(bulkAddSessionsSchema),
    defaultValues: {
      variationKey: "",
      displayTitle: "",
      description: "",
      eligibilityNotes: "",
      url: "",
      regUrl: "",
      recurrencePattern: "monday",
      availabilityStatus: defaultAvailabilityStatus,
      signupStatus: defaultSignupStatus,
      numDaysOverride: null,
      myOfferIncrementKey: null,
      createSingleDaysForOverlaps: false,
      regEvents: []
    }
  });
  
  // Now we can use form.watch since form is declared
  const selectedProgramKey = form.watch("variationKey");
  const previewStartDate = form.watch("startDate");
  const previewEndDate = form.watch("endDate");
  const previewRecurrencePattern = form.watch("recurrencePattern");
  const watchedDescription = form.watch("description") ?? "";
  const descriptionLength = watchedDescription.length;
  
  // Load decimal enums for increment calculations
  useEffect(() => {
    const loadEnums = async () => {
      try {
        const enums = await getDecimalEnums(db, "offerIncrement");
        const enumMap = new Map();
        enums.forEach(enumItem => {
          enumMap.set(enumItem.id, enumItem);
        });
        setEnumsMap(enumMap);
        bulkModalDevLog("[BulkAddSessionsModal] Loaded decimal enums:", enumMap);
      } catch (error) {
        console.error("[BulkAddSessionsModal] Error loading decimal enums:", error);
      }
    };
    loadEnums();
  }, []);
  
  // Inject Material Symbols font when modal opens (for lock icons)
  useEffect(() => {
    if (!isOpen) return;
    if (!document.querySelector('link[data-msymbols]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0';
      link.setAttribute('data-msymbols', '1');
      document.head.appendChild(link);
    }
  }, [isOpen]);

  // Small helper component for lock icon button
  const LockIconButton: React.FC<{
    name: 'lock' | 'lock_open_right';
    title: string;
    onClick: () => void;
    className?: string;
  }> = ({ name, title, onClick, className }) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        "absolute -top-3 right-1 z-10 cursor-pointer text-slate-500 hover:text-slate-700",
        className
      )}
    >
      <span
        className="material-symbols-outlined select-none"
        style={{ fontSize: 18, lineHeight: 1 }}
      >
        {name}
      </span>
    </button>
  );
  
  // Function to calculate increment options for a program
  const calculateIncrementOptions = useCallback((program: Variation) => {
    if (!program || enumsMap.size === 0) return [];
    
    bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Calculating increment options for program:", program);
    
    const options = [];
    
    // Add the default/regular increment option
    if (program.regularOfferIncrementKey) {
      const enumItem = enumsMap.get(program.regularOfferIncrementKey);
      if (enumItem) {
        options.push({
          value: program.regularOfferIncrementKey,
          label: enumItem.longLabel || enumItem.shortLabel || program.regularOfferIncrementKey,
          isDefault: true
        });
      }
    }
    
    // Add alternative increment options
    if (program.regularOfferIncrementAlternatives && program.regularOfferIncrementAlternatives.length > 0) {
      for (const alternative of program.regularOfferIncrementAlternatives) {
        const enumItem = enumsMap.get(alternative.key);
        if (enumItem) {
          options.push({
            value: alternative.key,
            label: enumItem.longLabel || enumItem.shortLabel || alternative.key,
            isDefault: false
          });
        }
      }
    }
    
    bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Calculated increment options:", options);
    return options;
  }, [enumsMap]);

  // JAIL BREAK: Calculate and update session length options instantly when program changes
  const updateSessionLengthInstantly = useCallback((programKey: string) => {
    if (!programKey || programs.length === 0) {
      setShowSessionLengthLocal(false);
      setSessionLengthOptionsLocal([]);
      setDefaultIncrementLocal("");
      return;
    }
    
    const selectedProgram = programs.find(program => program.key === programKey);
    if (!selectedProgram) {
      setShowSessionLengthLocal(false);
      setSessionLengthOptionsLocal([]);
      setDefaultIncrementLocal("");
      return;
    }
    
    bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Updating session length for program:", selectedProgram);
    
    const incrementOptions = calculateIncrementOptions(selectedProgram);
    const totalOptions = incrementOptions.length;
    
    bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Total increment options:", totalOptions);
    
    if (totalOptions > 1) {
      // Show session length selector when there are multiple options
      setShowSessionLengthLocal(true);
      setSessionLengthOptionsLocal(incrementOptions);
      
      // Set default to the program's default increment key
      setDefaultIncrementLocal(selectedProgram.regularOfferIncrementKey || "");
      
      bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Showing session length with options:", incrementOptions);
      bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Setting default increment:", selectedProgram.regularOfferIncrementKey);
    } else {
      // Hide session length selector when there's only 1 option (just the default)
      setShowSessionLengthLocal(false);
      setSessionLengthOptionsLocal([]);
      setDefaultIncrementLocal("");
      
      bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Hiding session length (only 1 option)");
    }
  }, [programs, calculateIncrementOptions]);

  // JAIL BREAK: Handle session length change
  const handleSessionLengthChange = useCallback((value: string) => {
    try {
      bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Session length changed to:", value);
      
      // Calculate numDays from the selected increment key
      const enumItem = enumsMap.get(value);
      if (enumItem && enumItem.value) {
        bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Setting numDaysOverride to:", enumItem.value);
        form.setValue("numDaysOverride", enumItem.value);
        
        // Store the selected increment key for database save
        bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Storing myOfferIncrementKey:", value);
        form.setValue("myOfferIncrementKey", value);
        
        // Debug: Verify the form values were set correctly
        bulkModalDevLog("🔍 DEBUGGING: Bulk form values after session length change:", {
          numDaysOverride: form.getValues("numDaysOverride"),
          myOfferIncrementKey: form.getValues("myOfferIncrementKey"),
          allFormValues: form.getValues()
        });
      }
    } catch (error) {
      console.error("[BulkAddSessionsModal] Error in handleSessionLengthChange:", error);
    }
  }, [form, enumsMap]);

  // JAIL BREAK: Initialize local state from form on program change
  useEffect(() => {
    if (selectedProgramKey && selectedProgramLocal !== selectedProgramKey) {
      bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Syncing local state:", selectedProgramKey);
      setSelectedProgramLocal(selectedProgramKey);
      updateSessionLengthInstantly(selectedProgramKey);
    }
  }, [selectedProgramKey, selectedProgramLocal, updateSessionLengthInstantly]);

  // JAIL BREAK: Initialize session length on enums load
  useEffect(() => {
    if (selectedProgramKey && enumsMap.size > 0 && programs.length > 0) {
      bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Updating session length display after enums loaded");
      updateSessionLengthInstantly(selectedProgramKey);
    }
  }, [enumsMap.size, programs, selectedProgramKey, updateSessionLengthInstantly]);
  
  // Preview of generated dates
  const [previewDates, setPreviewDates] = useState<Date[]>([]);
  // NEW: tab state for date input mode
  const [dateTab, setDateTab] = useState<string>("range");
  // NEW: date list input and parsed result
  const [dateListInput, setDateListInput] = useState<string>("RowFormat: ");
  const [parsedDateList, setParsedDateList] = useState<Date[]>([]);
  const [rowFormatSpecText, setRowFormatSpecText] = useState<string | null>(null);
  // KB-1460: per-session preview table — operator-excluded rows (by stable
  // `${epochKey}#${perDayIndex}` key). Only included rows are enqueued.
  const [excludedRowKeys, setExcludedRowKeys] = useState<Set<string>>(new Set());
  const [rowFormatSkipInfo, setRowFormatSkipInfo] = useState<{ unknownOfferingIdCount: number; missingOfferingIdCount: number } | null>(null);
  const [formatCheckResult, setFormatCheckResult] = useState<{
    checkedAt: Date;
    ok: boolean;
    issues: Array<{
      severity: "error" | "warning";
      message: string;
      lineNumber?: number;
      startDate?: string;
      title?: string;
    }>;
    summary: {
      parsedDateListCount: number;
      rowFormatSpecText: string | null;
      rowFormatSkipInfo: { unknownOfferingIdCount: number; missingOfferingIdCount: number } | null;
      dateListExtrasSummary: ReturnType<typeof debugSummarizeDateListExtras>;
    };
  } | null>(null);
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<{
    checkedAt: Date;
    ok: boolean;
    issues: Array<{
      severity: "error" | "warning";
      criterion: "1" | "2" | "3" | "4";
      message: string;
      lineNumbers: number[];
      sampleTitles?: string[];
    }>;
    summary: {
      parsedDateListCount: number;
      exactDuplicateCount: number;
      fuzzyDuplicateCount: number;
      fuzzyThreshold: number;
    };
  } | null>(null);
  const [formatCheckDetail, setFormatCheckDetail] = useState<{
    field: "description" | "eligibilityNotes" | "descriptionPrefix";
    dateKey: string;
    rowIndex: number;
    length: number;
    limit: number;
    title?: string;
    offeringId?: string;
    value: string;
  } | null>(null);

  const formatUtcDateKey = (dateKey: string) => {
    const ms = Number(dateKey);
    if (!Number.isFinite(ms)) return "Invalid dateKey";
    const d = new Date(ms);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth(); // 0-11
    const day = d.getUTCDate();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[m] ?? String(m + 1);
    return `${month} ${day}, ${y} (UTC)`;
  };

  // Helpers for forgiving parsing
  function normalizeTwoDigitYear(y: number) {
    if (y >= 0 && y <= 99) return 2000 + y;
    return y;
  }

  // Robust month-name mapping to 1..12, covering full and short forms (incl. "sept")
  function monthNameToIndex(raw: string): number {
    const m = raw.toLowerCase().replace(/\.$/, '');
    const map: Record<string, number> = {
      january: 1, jan: 1,
      february: 2, feb: 2,
      march: 3, mar: 3,
      april: 4, apr: 4,
      may: 5,
      june: 6, jun: 6,
      july: 7, jul: 7,
      august: 8, aug: 8,
      september: 9, sep: 9, sept: 9,
      october: 10, oct: 10,
      november: 11, nov: 11,
      december: 12, dec: 12,
    };
    return map[m] ?? -1;
  }

  function nextOccurrenceAfterToday(month: number, day: number) {
    const today = startOfToday();
    const thisYear = today.getFullYear();
    const candidate = new Date(thisYear, month - 1, day);
    if (isAfter(candidate, today)) return candidate;
    return addYears(candidate, 1);
  }

  function tryParseToken(token: string, carryMonth?: number): Date | null {
    const t = token.trim();
    if (!t) return null;

    // Numeric M/D[/YY|YYYY] at the start (allow trailing text)
    const full = /^(\d{1,2})[\/\-\.](\d{1,2})(?:[\/\-\.](\d{2,4}))?/.exec(t);
    if (full) {
      const m = parseInt(full[1], 10);
      const d = parseInt(full[2], 10);
      const yy = full[3];
      if (yy) {
        const yr = yy.length === 2 ? normalizeTwoDigitYear(parseInt(yy, 10)) : parseInt(yy, 10);
        const dt = new Date(yr, m - 1, d);
        return isValid(dt) ? dt : null;
      } else {
        return nextOccurrenceAfterToday(m, d);
      }
    }

    // Month name forms: "February 1", "Feb 1", "Feb. 1"
    const m = /^(?<mon>[A-Za-z]+)\.?\s+(?<day>\d{1,2})(?:\s*,?\s*(?<year>\d{2,4}))?/.exec(t);
    if (m && m.groups) {
      const monthIndex = monthNameToIndex(m.groups.mon);
      if (monthIndex > 0) {
        const d = parseInt(m.groups.day, 10);
        const y = m.groups.year;
        if (y) {
          const yr = y.length === 2 ? normalizeTwoDigitYear(parseInt(y, 10)) : parseInt(y, 10);
          const dt = new Date(yr, monthIndex - 1, d);
          return isValid(dt) ? dt : null;
        } else {
          return nextOccurrenceAfterToday(monthIndex, d);
        }
      }
    }

    const onlyDay = /^(\d{1,2})$/.exec(t);
    if (onlyDay && carryMonth) {
      const d = parseInt(onlyDay[1], 10);
      return nextOccurrenceAfterToday(carryMonth, d);
    }

    return null;
  }

  // Parse a leading date from a free-form part. Returns consumed chars and month used.
  function parseLeadingDate(part: string): { date: Date | null; usedMonth?: number; consumed: number } {
    const s = part.trimStart();
    const offset = part.length - s.length; // leading whitespace consumed

    // Try month-name first
    const m = /^(?<mon>[A-Za-z]+)\.?\s+(?<day>\d{1,2})(?:\s*,?\s*(?<year>\d{2,4}))?/.exec(s);
    if (m && m.groups) {
      const day = parseInt(m.groups.day, 10);
      const monthIndex = monthNameToIndex(m.groups.mon);
      if (monthIndex > 0) {
        const y = m.groups.year;
        if (y) {
          const yr = y.length === 2 ? normalizeTwoDigitYear(parseInt(y, 10)) : parseInt(y, 10);
          const dt = new Date(yr, monthIndex - 1, day);
          if (isValid(dt)) return { date: dt, usedMonth: monthIndex, consumed: offset + m[0].length };
        } else {
          const dt = nextOccurrenceAfterToday(monthIndex, day);
          return { date: dt, usedMonth: monthIndex, consumed: offset + m[0].length };
        }
      }
    }
    // Try ISO date at start: YYYY-MM-DD (or YYYY/MM/DD)
    const iso = /^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})/.exec(s);
    if (iso) {
      const yr = parseInt(iso[1], 10);
      const mth = parseInt(iso[2], 10);
      const day = parseInt(iso[3], 10);
      const dt = new Date(yr, mth - 1, day);
      if (isValid(dt)) return { date: dt, usedMonth: mth, consumed: offset + iso[0].length };
    }
    // Try numeric at start
    const n = /^(\d{1,2})[\/\-\.](\d{1,2})(?:[\/\-\.](\d{2,4}))?/.exec(s);
    if (n) {
      const mth = parseInt(n[1], 10);
      const day = parseInt(n[2], 10);
      const yy = n[3];
      if (yy) {
        const yr = yy.length === 2 ? normalizeTwoDigitYear(parseInt(yy, 10)) : parseInt(yy, 10);
        const dt = new Date(yr, mth - 1, day);
        if (isValid(dt)) return { date: dt, usedMonth: mth, consumed: offset + n[0].length };
      } else {
        const dt = nextOccurrenceAfterToday(mth, day);
        return { date: dt, usedMonth: mth, consumed: offset + n[0].length };
      }
    }
    return { date: null, consumed: 0 };
  }

  // Represent a calendar day as a Date pinned to noon UTC.
  // This avoids timezone-dependent day rollover when dates are later converted to
  // ISO day strings via UTC getters (e.g. reconcile Tier1 keys).
  function dateAtUtcNoonForLocalDay(d: Date): Date {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return d;
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0));
  }

  // Admin-only row format parsing
  function parseRowFormatSpec(line: string) {
    // RowFormat supports placeholders like: <title>, <title.suffix>, <url>, <description>, <description.prefix>, <eligibilityNotes>, <offeringId>, <locationId>, <startDate>, <endDate>
    // Identify fields and separators; supports quoted literals as separators. The asterisk `*` acts as a wildcard segment.
    const m = /^RowFormat\s*:\s*(.+)$/i.exec(line.trim());
    if (!m) return null;
    const spec = m[1];
    // Capture ordered placeholders and separators between them
    const tokens: Array<{ type: 'field' | 'sep'; value: string }> = [];
    const fieldRe = /<([^>]+)>|"([^"]*)"/g; // match <field> or "literal"
    let lastIndex = 0;
    let mm: RegExpExecArray | null;
    while ((mm = fieldRe.exec(spec)) !== null) {
      if (mm.index > lastIndex) {
        tokens.push({ type: 'sep', value: spec.slice(lastIndex, mm.index) });
      }
      const fieldName = mm[1] || (mm[2] ? `literal:${mm[2]}` : '');
      tokens.push({ type: 'field', value: fieldName });
      lastIndex = fieldRe.lastIndex;
    }
    if (lastIndex < spec.length) {
      tokens.push({ type: 'sep', value: spec.slice(lastIndex) });
    }
    // Build a regex that tolerates spaces around separators. Treat '*' in separators as wildcard (non-capturing)
    const parts = tokens.map(t => {
      if (t.type === 'sep') {
        if (t.value.includes('*')) {
          const segments = t.value
            .split('*')
            .map(seg => seg.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '\\s*'));
          return segments.join('(?:.+?)');
        }
        return t.value.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '\\s*');
      }
      // Required non-empty fields
      if (t.value === 'title') return '(.+?)';
      if (t.value === 'startDate') return '(.+?)';
      // Optional fields: allow empty strings
      if (t.value === 'endDate') return '(.*?)';
      if (t.value === 'numDays') return '(.*?)'; // NEW: support <numDays> placeholder
      if (t.value === 'price') return '(.*?)'; // NEW: support <price> placeholder
      if (t.value === 'price.singleDay') return '(.*?)'; // NEW: support <price.singleDay> placeholder
      if (t.value === 'priceTier2') return '(.*?)'; // NEW: support <priceTier2> placeholder
      if (t.value === 'priceTier3') return '(.*?)'; // NEW: support <priceTier3> placeholder
      if (
        t.value === 'url' ||
        t.value === 'regUrl' ||
        t.value === 'description' ||
        t.value === 'description.prefix' ||
        t.value === 'eligibilityNotes' ||
        t.value === 'priceNote' ||
        t.value === 'timingNote' ||
        t.value === 'minAge' ||
        t.value === 'maxAge' ||
        t.value === 'minGradeKey' ||
        t.value === 'maxGradeKey' ||
        t.value === 'locationId' ||
        t.value === 'offeringId' ||
        t.value === 'title.suffix' ||
        t.value === 'name.suffix'
      ) return '(.*?)';
      // NEW: Optional status placeholders
      if (t.value === 'regStatus' || t.value === 'availStatus') return '(.*?)';
      // NEW: Optional registration event placeholders (up to 3 events)
      if (t.value === 'regEvent1' || t.value === 'regEvent2' || t.value === 'regEvent3') return '(.*?)';
      if (t.value === 'regEvent1.datetime' || t.value === 'regEvent2.datetime' || t.value === 'regEvent3.datetime') return '(.*?)';
      if (t.value === 'regEvent1.note' || t.value === 'regEvent2.note' || t.value === 'regEvent3.note') return '(.*?)';
      // Treat literal as optional non-empty capture (we ignore captured value and use the literal itself later)
      if (t.value.startsWith('literal:')) return '(.*?)';
      // Default to optional capture to be permissive
      return '(.*?)';
    });
    // Strictly anchor to the full line; allow surrounding whitespace
    // IMPORTANT: enable dotAll ('s') so field captures like description can span newlines
    const pattern = new RegExp('^\\s*' + parts.join('') + '\\s*$', 'is');
    const fieldsOrder = tokens.filter(t => t.type === 'field').map(t => t.value);
    return { pattern, fieldsOrder, specRaw: spec.trim() };
  }

  function parseDateListInput(input: string): Date[] {
    if (input.length > BULK_DATE_LIST_MAX_INPUT_CHARS) {
      setRowFormatSpecText(null);
      setRowFormatSkipInfo(null);
      form.setValue("dateListExtras" as any, {}, { shouldValidate: false });
      return [];
    }

    const lines = input.split(/\r?\n/);
    const dates: Date[] = [];
    const seen = new Set<number>();
    const validOfferingIds = new Set((programs || []).map(p => p.key));
    let unknownOfferingIdCount = 0;
    let missingOfferingIdCount = 0;

    const parseAgeOverride = (raw?: string): number | undefined => {
      const v = (raw ?? '').trim();
      if (!v) return undefined;
      if (v.toLowerCase() === 'default') return undefined;
      const n = parseFloat(v);
      if (!Number.isFinite(n) || n < 0) return undefined;
      return n;
    };
    const parseGradeKeyOverride = (raw?: string): string | undefined => {
      const v = (raw ?? '').trim();
      if (!v) return undefined;
      if (v.toLowerCase() === 'default') return undefined;
      return v;
    };
    // In list mode we attach optional per-date extras used downstream when
    // creating offers. For compatibility we allow either:
    // - a single extras object per date (legacy / single row), or
    // - an array of extras objects when multiple RowFormat rows share the same date.
    const extrasByKey: Record<
      string,
      | {
          locationId?: string;
          title?: string;
          titleSuffix?: string;
          url?: string;
          regUrl?: string;
          description?: string;
          descriptionPrefix?: string;
          eligibilityNotes?: string;
          priceNote?: string;
          timingNote?: string;
          offeringId?: string;
          minAgeInYearsOverride?: number | null;
          maxAgeInYearsOverride?: number | null;
          minAgeGradeKeyOverride?: string;
          maxAgeGradeKeyOverride?: string;
          endDate?: Date;
          availabilityStatus?: string;
          signupStatus?: string;
          regEvents?: Array<{ eventKey: string; when?: Date; wasTimeProvided: boolean; note: string }>;
          overrideNumDays?: number | null;
          priceOverride?: number;
          priceSingleDayOverride?: number;
          priceRegularTier2Override?: number | null;
          priceRegularTier3Override?: number | null;
        }
      | Array<{
          locationId?: string;
          title?: string;
          titleSuffix?: string;
          url?: string;
          regUrl?: string;
          description?: string;
          descriptionPrefix?: string;
          eligibilityNotes?: string;
          priceNote?: string;
          timingNote?: string;
          offeringId?: string;
          minAgeInYearsOverride?: number | null;
          maxAgeInYearsOverride?: number | null;
          minAgeGradeKeyOverride?: string;
          maxAgeGradeKeyOverride?: string;
          endDate?: Date;
          availabilityStatus?: string;
          signupStatus?: string;
          regEvents?: Array<{ eventKey: string; when?: Date; wasTimeProvided: boolean; note: string }>;
          overrideNumDays?: number | null;
          priceOverride?: number;
          priceSingleDayOverride?: number;
          priceRegularTier2Override?: number | null;
          priceRegularTier3Override?: number | null;
        }>
    > = {};

    // Helper to translate CSV-style doubled double-quotes ("") into a single "
    // so that values like ""CIT"" become "CIT" when captured via RowFormat.
    const normalizeDoubleQuotes = (s: string): string => s.replace(/""/g, '"');

    // Check for admin RowFormat spec in first non-empty line
    let rowFormat: ReturnType<typeof parseRowFormatSpec> | null = null;
    const firstNonEmptyIndex = lines.findIndex(l => l.trim().length > 0);
    const firstNonEmpty = firstNonEmptyIndex >= 0 ? lines[firstNonEmptyIndex] : '';
    rowFormat = parseRowFormatSpec(firstNonEmpty);

    let startIndex = rowFormat ? firstNonEmptyIndex + 1 : 0;

    // If there's no explicit RowFormat, attempt to auto-detect camp-data's pipe table formats
    // so admins can paste `finalized_output.txt` directly (wrappedCurly or standard pipe table).
    if (!rowFormat) {
      const headerTokenToRowFormatField = (raw: string, idx: number): string => {
        const t = String(raw || '').trim().replace(/[<>]/g, '');
        const k = t.toLowerCase();
        if (k === 'offeringid') return 'offeringId';
        if (k === 'locationid') return 'locationId';
        if (k === 'availstr') return 'availStr';
        if (k === 'availstatus') return 'availStatus';
        if (k === 'regstatus') return 'regStatus';
        if (k === 'startdate') return 'startDate';
        if (k === 'enddate') return 'endDate';
        if (k === 'numdays') return 'numDays';
        if (k === 'price') return 'price';
        if (k === 'pricenote') return 'priceNote';
        if (k === 'minage') return 'minAge';
        if (k === 'maxage') return 'maxAge';
        if (k === 'mingradekey') return 'minGradeKey';
        if (k === 'maxgradekey') return 'maxGradeKey';
        if (k === 'title') return 'title';
        if (k === 'description') return 'description';
        if (k === 'timingnote') return 'timingNote';
        if (k === 'url') return 'url';
        if (k === 'regurl') return 'regUrl';
        if (k === 'eligibilitynotes') return 'eligibilityNotes';
        // Unknown column: keep it as a placeholder so column-count alignment still works;
        // the parser will capture it but ignore it downstream.
        return `_skip${idx}`;
      };

      const tryAutoDetectCampDataTable = (): { rowFormat: ReturnType<typeof parseRowFormatSpec>; headerLineIndex: number } | null => {
        for (let i = 0; i < lines.length; i++) {
          const trimmed = (lines[i] || '').trim();
          if (!trimmed) continue;
          // Do not try to auto-detect if the user actually provided RowFormat somewhere up top.
          if (/^RowFormat\s*:/i.test(trimmed)) return null;

          const isWrappedCurly = trimmed.startsWith('{{{|') && trimmed.endsWith('|}}}');
          const isPipeTable = trimmed.startsWith('|') && trimmed.endsWith('|');
          if (!isWrappedCurly && !isPipeTable) continue;

          const prefix = isWrappedCurly ? '{{{|' : '|';
          const suffix = isWrappedCurly ? '|}}}' : '|';
          const inner = trimmed.slice(prefix.length, trimmed.length - suffix.length);
          const headerTokens = inner.split('|').map(s => s.trim()).filter(Boolean);
          if (headerTokens.length < 2) continue;

          const normalized = headerTokens.map(h => String(h).trim().replace(/[<>]/g, '').toLowerCase());
          const hasStartDate = normalized.includes('startdate');
          const hasTitle = normalized.includes('title');
          if (!hasStartDate || !hasTitle) continue;

          const fields = headerTokens.map((h, idx) => headerTokenToRowFormatField(h, idx));
          const spec = `${prefix}${fields.map(f => `<${f}>`).join('|')}${suffix}`;
          const rf = parseRowFormatSpec(`RowFormat: ${spec}`);
          if (!rf) continue;
          return { rowFormat: rf, headerLineIndex: i };
        }
        return null;
      };

      const detected = tryAutoDetectCampDataTable();
      if (detected) {
        rowFormat = detected.rowFormat;
        startIndex = detected.headerLineIndex + 1; // skip header row
      }
    }

    setRowFormatSpecText(rowFormat ? rowFormat.specRaw : null);
    const isCrossProgramMode = !!rowFormat?.fieldsOrder?.includes('offeringId');

    const rowFormatSpecTrimmed = rowFormat?.specRaw?.trim() ?? "";
    /** Camp-data `{{{|...|}}}` tables: never use dotAll RowFormat regex for assembly/match — it can take minutes on real pastes. */
    const isWrappedCurlyPipeCampTable =
      !!rowFormat &&
      rowFormatSpecTrimmed.startsWith("{{{|") &&
      rowFormatSpecTrimmed.endsWith("|}}}");

    const trySplitWrappedCurlyPipeCells = (logicalRow: string, fieldsOrder: string[]): string[] | null => {
      const fieldCount = fieldsOrder.length;
      const trimmed = logicalRow.trim();
      if (!trimmed.startsWith("{{{|") || !trimmed.endsWith("|}}}")) return null;
      const inner = trimmed.slice(4, trimmed.length - 4);
      let parts = inner.split("|");

      // Some camp-data exports include <regStatus> in the header but omit the column in data rows
      // (one fewer "|" than the header). Without this, split length !== fieldCount and we fall back
      // to dotAll RowFormat regex on multiline rows (UI freeze).
      if (parts.length === fieldCount - 1) {
        const rsi = fieldsOrder.indexOf("regStatus");
        const minIdx = fieldsOrder.indexOf("minAge");
        const maxIdx = fieldsOrder.indexOf("maxAge");
        if (rsi >= 0 && minIdx === rsi + 1 && maxIdx === rsi + 2 && rsi < parts.length) {
          const a = (parts[rsi] ?? "").trim();
          const b = (parts[rsi + 1] ?? "").trim();
          const prev = rsi > 0 ? (parts[rsi - 1] ?? "").trim() : "";
          if (/^\d{1,3}$/.test(a) && /^\d{1,3}$/.test(b) && !/^\d+$/.test(prev)) {
            parts = [...parts.slice(0, rsi), "", ...parts.slice(rsi)];
          }
        }
      }

      if (parts.length !== fieldCount) return null;
      return parts;
    };

    // When RowFormat is provided, support multi-line rows.
    // Build row chunks by accumulating physical lines until the RowFormat pattern matches.
    // This allows fields like <description> to legally contain line breaks (char(10)).
    const iterRows = () => {
      if (!rowFormat) return null;
      const rows: string[] = [];
      let buffer = "";
      let physLinesInBuffer = 0;

      // Fast path: camp-data wrapped-curly rows always end a logical row with "|}}}" — no regex on megabyte buffers.
      if (isWrappedCurlyPipeCampTable) {
        for (let i = startIndex; i < lines.length; i++) {
          const raw = lines[i];
          const trimmed = raw.trim();
          if (!buffer) {
            if (!trimmed) continue;
            if (raw.length > BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS) continue;
            buffer = raw;
            physLinesInBuffer = 1;
          } else {
            buffer += "\n" + raw;
            physLinesInBuffer += 1;
          }
          if (
            physLinesInBuffer > BULK_DATE_LIST_MAX_PHYS_LINES_PER_LOGICAL_ROW ||
            buffer.length > BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS
          ) {
            buffer = "";
            physLinesInBuffer = 0;
            if (raw.length > BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS) continue;
            if (!trimmed) continue;
            buffer = raw;
            physLinesInBuffer = 1;
          }
          if (/\|\}\}\}\s*$/.test(buffer.trimEnd())) {
            rows.push(buffer);
            buffer = "";
            physLinesInBuffer = 0;
          }
        }
        return rows;
      }

      const candidateFromBuffer = (buf: string) => {
        const bufferTrimmed = buf.trim();
        return bufferTrimmed.startsWith('"') && bufferTrimmed.endsWith('"')
          ? bufferTrimmed.slice(1, -1)
          : buf;
      };

      for (let i = startIndex; i < lines.length; i++) {
        const raw = lines[i];
        const trimmed = raw.trim();

        // If we don't yet have a buffer started, skip completely empty lines
        if (!buffer) {
          if (!trimmed) continue;
          if (raw.length > BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS) continue;
          buffer = raw;
          physLinesInBuffer = 1;
        } else {
          buffer += "\n" + raw;
          physLinesInBuffer += 1;
        }

        // Abort runaway accumulation: wrong RowFormat / partial paste would otherwise freeze the UI.
        if (
          physLinesInBuffer > BULK_DATE_LIST_MAX_PHYS_LINES_PER_LOGICAL_ROW ||
          buffer.length > BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS
        ) {
          buffer = "";
          physLinesInBuffer = 0;
          if (raw.length > BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS) continue;
          if (!trimmed) continue;
          buffer = raw;
          physLinesInBuffer = 1;
        }

        const candidate = candidateFromBuffer(buffer);
        if (rowFormat.pattern.test(candidate)) {
          rows.push(buffer);
          buffer = "";
          physLinesInBuffer = 0;
        }
      }

      if (buffer) {
        if (
          buffer.length <= BULK_DATE_LIST_MAX_ROW_BUFFER_CHARS &&
          physLinesInBuffer <= BULK_DATE_LIST_MAX_PHYS_LINES_PER_LOGICAL_ROW
        ) {
          const candidate = candidateFromBuffer(buffer);
          if (rowFormat.pattern.test(candidate)) {
            rows.push(buffer);
          }
        }
      }

      return rows;
    };

    const rowLines = rowFormat ? iterRows() : null;

    // KB-4101 — a row override must be canonical as well as listed: a legacy option doc in Firestore
    // would otherwise reach dateListExtras, where the schema rejects it with no visible field message.
    const validAvailIds = new Set((availabilityStatusOptions || []).map((o) => o.id).filter(isAvailabilityStatus));
    const validSignupIds = new Set((signupStatusOptions || []).map((o) => o.id).filter(isSignupStatus));

    const loopStart = rowFormat ? 0 : startIndex;
    const loopEnd = rowFormat ? (rowLines as string[]).length : lines.length;
    for (let i = loopStart; i < loopEnd; i++) {
      const rawLine = rowFormat ? (rowLines as string[])[i] : lines[i];
      const lineTrimmed = rawLine.trim();
      if (!lineTrimmed) continue; // allow empty rows

      // If RowFormat is provided, only accept rows that fully match it.
      if (rowFormat) {
        // Strip surrounding quotes if present to allow multi-line field matching
        const line = (lineTrimmed.startsWith('"') && lineTrimmed.endsWith('"'))
          ? lineTrimmed.slice(1, -1)
          : rawLine;

        let pipeCells: string[] | null = null;
        if (isWrappedCurlyPipeCampTable) {
          pipeCells = trySplitWrappedCurlyPipeCells(line, rowFormat.fieldsOrder);
        }
        const mm = pipeCells ? null : rowFormat.pattern.exec(line);
        if (!pipeCells && !mm) {
          continue;
        }

        let suffix: string | undefined;
        let titleVal: string | undefined;
        let urlVal: string | undefined;
        let descriptionVal: string | undefined;
        let descriptionPrefixVal: string | undefined;
        let eligibilityNotesVal: string | undefined;
        let priceNoteVal: string | undefined;
        let timingNoteVal: string | undefined;
        let offeringIdVal: string | undefined;
        let locationIdVal: string | undefined;
        let minAgeVal: string | undefined;
        let maxAgeVal: string | undefined;
        let minGradeKeyVal: string | undefined;
        let maxGradeKeyVal: string | undefined;
        let regUrlVal: string | undefined;
        let startStr: string | undefined;
        let endStr: string | undefined;
        let numDaysVal: string | undefined; // NEW
        let priceVal: string | undefined; // NEW: price value
        let priceSingleDayVal: string | undefined; // NEW: single-day price value
        let priceTier2Val: string | undefined;
        let priceTier3Val: string | undefined;
        let regStatusVal: string | undefined;
        let availStatusVal: string | undefined;
        
        // Registration event fields
        const regEventFields: Array<{
          eventKey?: string;
          datetime?: string;
          note?: string;
        }> = [{}, {}, {}]; // Support up to 3 events
        let captureIdx = 0;
        for (const f of rowFormat.fieldsOrder) {
          // Preserve empty strings for optional fields by not trimming away empties entirely
          const captured = pipeCells
            ? (pipeCells[captureIdx++] ?? "")
            : (mm![captureIdx++ + 1] ?? "");
          const rawVal = typeof captured === 'string' ? captured.trim() : '';
          const val = rawVal ? normalizeDoubleQuotes(rawVal) : rawVal;
          if (f === 'title') {
            titleVal = val; // keep even if empty to indicate override
          } else if (f === 'title.suffix' || f === 'name.suffix' || f.startsWith('literal:')) {
            suffix = f.startsWith('literal:') ? f.slice('literal:'.length) : val;
          } else if (f === 'url') {
            // Sentinel "<progDefault>" means: do not override program defaults (treat as absent)
            urlVal = isProgDefaultSentinel(val) ? undefined : val; // keep empty-string override behavior
          } else if (f === 'description') {
            // Sentinel "<progDefault>" means: do not override program defaults (treat as absent)
            descriptionVal = isProgDefaultSentinel(val) ? undefined : val; // keep empty-string override behavior
          } else if (f === 'description.prefix') {
            // Row-level description prefix that will be combined server-side
            // Sentinel "<progDefault>" means: do not override (treat as absent)
            descriptionPrefixVal = isProgDefaultSentinel(val) ? undefined : val; // keep empty-string override behavior
          } else if (f === 'eligibilityNotes') {
            // Sentinel "<progDefault>" means: do not override program defaults (treat as absent)
            eligibilityNotesVal = isProgDefaultSentinel(val) ? undefined : val; // keep empty-string override behavior
          } else if (f === 'priceNote') {
            // Sentinel "<progDefault>" means: do not override program defaults (treat as absent)
            priceNoteVal = isProgDefaultSentinel(val) ? undefined : val; // keep empty-string override behavior
          } else if (f === 'timingNote') {
            // Sentinel "<progDefault>" means: do not override program defaults (treat as absent)
            timingNoteVal = isProgDefaultSentinel(val) ? undefined : val; // keep empty-string override behavior
          } else if (f === 'offeringId') {
            offeringIdVal = val;
          } else if (f === 'locationId') {
            locationIdVal = val;
          } else if (f === 'minAge') {
            minAgeVal = val;
          } else if (f === 'maxAge') {
            maxAgeVal = val;
          } else if (f === 'minGradeKey') {
            minGradeKeyVal = val;
          } else if (f === 'maxGradeKey') {
            maxGradeKeyVal = val;
          } else if (f === 'regUrl') {
            // Sentinel "<progDefault>" means: do not override program defaults (treat as absent)
            regUrlVal = isProgDefaultSentinel(val) ? undefined : val; // keep empty-string override behavior
          } else if (f === 'regStatus') {
            regStatusVal = val;
          } else if (f === 'availStatus' || f === 'avStatus') {
            availStatusVal = val;
          } else if (f === 'regEvent1') {
            regEventFields[0].eventKey = val;
          } else if (f === 'regEvent1.datetime') {
            regEventFields[0].datetime = val;
          } else if (f === 'regEvent1.note') {
            regEventFields[0].note = val;
          } else if (f === 'regEvent2') {
            regEventFields[1].eventKey = val;
          } else if (f === 'regEvent2.datetime') {
            regEventFields[1].datetime = val;
          } else if (f === 'regEvent2.note') {
            regEventFields[1].note = val;
          } else if (f === 'regEvent3') {
            regEventFields[2].eventKey = val;
          } else if (f === 'regEvent3.datetime') {
            regEventFields[2].datetime = val;
          } else if (f === 'regEvent3.note') {
            regEventFields[2].note = val;
          } else if (f === 'startDate') {
            startStr = val;
          } else if (f === 'endDate') {
            endStr = val;
          } else if (f === 'numDays') { // NEW
            numDaysVal = val;
          } else if (f === 'price') { // NEW: price value
            priceVal = val;
          } else if (f === 'price.singleDay') { // NEW: single-day price value
            priceSingleDayVal = val;
          } else if (f === 'priceTier2') {
            priceTier2Val = val;
          } else if (f === 'priceTier3') {
            priceTier3Val = val;
          }
        }

        // Cross-program mode: offeringId is required.
        //
        // IMPORTANT: In org-bulk / cross-location workflows the modal may not have a complete
        // program list (e.g. dummy location) and/or the per-row <locationId> may point at a
        // different location than the currently viewed one. In those cases, validating the
        // offeringId against the current location's `variations` would incorrectly skip all rows.
        //
        // So we only enforce "known offeringId" when:
        // - we actually have a non-empty local program list, AND
        // - the RowFormat is NOT targeting other locations via <locationId>.
        if (isCrossProgramMode) {
          const off = (offeringIdVal || '').trim();
          if (!off) {
            missingOfferingIdCount += 1;
            continue;
          }

          const isCrossLocationMode = !!rowFormat?.fieldsOrder?.includes("locationId");
          const shouldValidateAgainstLocalPrograms = validOfferingIds.size > 0 && !isCrossLocationMode;

          if (shouldValidateAgainstLocalPrograms && !validOfferingIds.has(off)) {
            unknownOfferingIdCount += 1;
            continue;
          }

          offeringIdVal = off;
        }
        // Require start date; title can be empty but row still valid as long as date exists
        if (!startStr) continue; // must have a start date
        const lead = parseLeadingDate(startStr);
        if (!lead.date) continue; // invalid start
        const startDate = dateAtUtcNoonForLocalDay(lead.date);
        const keyNum = utcEpochDayKeyNumber(startDate);
        const k = String(keyNum);
        // IMPORTANT: In RowFormat mode we DO NOT de-duplicate by date.
        // Each physical row produces a session, even if multiple rows share
        // the same startDate. We still keep a numeric key for extras mapping.
        dates.push(startDate);

        // Prepare a per-row extras object and attach it to extrasByKey[k],
        // preserving legacy behavior (single object) when there is only
        // a single row for that date and switching to an array when there
        // are multiple rows for the same date.
        const rowExtras: {
          locationId?: string;
          title?: string;
          titleSuffix?: string;
          url?: string;
          regUrl?: string;
          description?: string;
          descriptionPrefix?: string;
          eligibilityNotes?: string;
          priceNote?: string;
          timingNote?: string;
          offeringId?: string;
          minAgeInYearsOverride?: number | null;
          maxAgeInYearsOverride?: number | null;
          minAgeGradeKeyOverride?: string;
          maxAgeGradeKeyOverride?: string;
          endDate?: Date;
          availabilityStatus?: string;
          signupStatus?: string;
          regEvents?: Array<{ eventKey: string; when?: Date; wasTimeProvided: boolean; note: string }>;
          overrideNumDays?: number | null;
          priceOverride?: number;
          priceSingleDayOverride?: number;
          priceRegularTier2Override?: number | null;
          priceRegularTier3Override?: number | null;
        } = {};

        const existing = extrasByKey[k];
        if (!existing) {
          extrasByKey[k] = rowExtras;
        } else if (Array.isArray(existing)) {
          existing.push(rowExtras);
        } else {
          extrasByKey[k] = [existing, rowExtras];
        }

        if (locationIdVal !== undefined) {
          const loc = String(locationIdVal ?? '').trim();
          // Preserve empty-string override semantics? For locationId we treat empty as "unset".
          if (loc.length > 0) rowExtras.locationId = loc;
        }
        if (titleVal !== undefined) rowExtras.title = titleVal;
        if (suffix) rowExtras.titleSuffix = suffix;
        if (urlVal !== undefined) rowExtras.url = urlVal;
        if (descriptionVal !== undefined) rowExtras.description = descriptionVal;
        if (descriptionPrefixVal !== undefined) rowExtras.descriptionPrefix = descriptionPrefixVal;
        if (eligibilityNotesVal !== undefined) rowExtras.eligibilityNotes = eligibilityNotesVal;
        if (priceNoteVal !== undefined) rowExtras.priceNote = priceNoteVal;
        if (timingNoteVal !== undefined) rowExtras.timingNote = timingNoteVal;
        if (offeringIdVal !== undefined) rowExtras.offeringId = offeringIdVal;
        if (regUrlVal !== undefined) rowExtras.regUrl = regUrlVal;

        // NEW: Per-row age overrides (<minAge>/<maxAge>) — blank/default means no override.
        const minParsed = parseAgeOverride(minAgeVal);
        if (minParsed !== undefined) rowExtras.minAgeInYearsOverride = minParsed;
        const maxParsed = parseAgeOverride(maxAgeVal);
        if (maxParsed !== undefined) rowExtras.maxAgeInYearsOverride = maxParsed;
        // NEW: Per-row grade-key overrides (<minGradeKey>/<maxGradeKey>) — blank/default means no override.
        const minGradeParsed = parseGradeKeyOverride(minGradeKeyVal);
        if (minGradeParsed !== undefined) rowExtras.minAgeGradeKeyOverride = minGradeParsed;
        const maxGradeParsed = parseGradeKeyOverride(maxGradeKeyVal);
        if (maxGradeParsed !== undefined) rowExtras.maxAgeGradeKeyOverride = maxGradeParsed;
        // Only set statuses if they are valid known IDs for the proper type
        if (isSignupStatus(regStatusVal) && validSignupIds.has(regStatusVal)) {
          rowExtras.signupStatus = regStatusVal;
        }
        if (isAvailabilityStatus(availStatusVal) && validAvailIds.has(availStatusVal)) {
          rowExtras.availabilityStatus = availStatusVal;
        }
        
        // Process registration events
        const regEvents: Array<{ eventKey: string; when?: Date; wasTimeProvided: boolean; note: string }> = [];
        for (const regEvent of regEventFields) {
          // Accept any non-empty eventKey; full validation happens server-side
          if (regEvent.eventKey && regEvent.eventKey.trim().length > 0) {
            const event: { eventKey: string; when?: Date; wasTimeProvided: boolean; note: string } = {
              eventKey: regEvent.eventKey,
              wasTimeProvided: false,
              note: regEvent.note || ''
            };
            
            if (regEvent.datetime) {
              const { when, wasTimeProvided } = parseRegEventDateTime(regEvent.datetime);
              if (when) {
                event.when = when;
                event.wasTimeProvided = wasTimeProvided;
              }
            }
            
            regEvents.push(event);
          }
        }
        
        if (regEvents.length > 0) {
          rowExtras.regEvents = regEvents;
        }
        let endLead: ReturnType<typeof parseLeadingDate> | undefined;
        if (endStr) {
            endLead = parseLeadingDate(endStr);
            if (endLead.date) {
              rowExtras.endDate = dateAtUtcNoonForLocalDay(endLead.date);
            }
        }

        // NEW: Compute overrideNumDays from <numDays>
        if (typeof numDaysVal === 'string') {
          const v = numDaysVal.trim();
          if (v.length > 0) {
            if (v.toLowerCase() === 'datediff') {
              if (lead.date && endLead?.date) {
                const startMs = Date.UTC(lead.date.getFullYear(), lead.date.getMonth(), lead.date.getDate());
                const endMs = Date.UTC(endLead.date.getFullYear(), endLead.date.getMonth(), endLead.date.getDate());
                const diffDays = Math.floor((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
                if (diffDays >= 1) {
                  rowExtras.overrideNumDays = diffDays;
                }
              }
            } else {
              const n = parseInt(v, 10);
              if (Number.isFinite(n) && n >= 1) {
                rowExtras.overrideNumDays = n;
              }
            }
          }
        }

        // NEW: Compute priceOverride from <price>
        if (typeof priceVal === 'string') {
          const v = priceVal.trim();
          if (v.length > 0) {
            const priceNum = parseFloat(v);
            if (Number.isFinite(priceNum) && priceNum >= 0) {
              rowExtras.priceOverride = priceNum;
            }
          }
        }
        // NEW: Compute priceSingleDayOverride from <price.singleDay>
        if (typeof priceSingleDayVal === 'string') {
          const v = priceSingleDayVal.trim();
          if (v.length > 0) {
            const priceNum = parseFloat(v);
            if (Number.isFinite(priceNum) && priceNum >= 0) {
              rowExtras.priceSingleDayOverride = priceNum;
            }
          }
        }
        // NEW: Compute priceRegularTier2Override from <priceTier2>, priceRegularTier3Override from <priceTier3>
        if (typeof priceTier2Val === 'string') {
          const v = priceTier2Val.trim();
          if (v.length > 0) {
            const num = parseFloat(v);
            if (Number.isFinite(num) && num >= 0) {
              rowExtras.priceRegularTier2Override = num;
            }
          }
        }
        if (typeof priceTier3Val === 'string') {
          const v = priceTier3Val.trim();
          if (v.length > 0) {
            const num = parseFloat(v);
            if (Number.isFinite(num) && num >= 0) {
              rowExtras.priceRegularTier3Override = num;
            }
          }
        }
        continue; // proceed to next row; skip generic parsing entirely
      }

      // No RowFormat: parse flexibly, allowing multiple dates per line and carryover
      const parts = lineTrimmed.split(/[\,\.]+/).map(p => p.trim()).filter(Boolean);
      let carryMonth: number | undefined = undefined;
      for (const part of parts) {
        const lead = parseLeadingDate(part);
        if (lead.date) {
          const key = utcEpochDayKeyNumber(lead.date);
          if (!seen.has(key)) { seen.add(key); dates.push(lead.date); }
          if (lead.usedMonth) carryMonth = lead.usedMonth;
        }
        const remainder = lead.consumed > 0 ? part.slice(lead.consumed) : part;
        const tokens = remainder.split(/\s+/).filter(Boolean);
        for (const tok of tokens) {
          const numFull = /^(\d{1,2})[\/\-\.](\d{1,2})(?:[\/\-\.](\d{2,4}))?$/.exec(tok);
          if (numFull) carryMonth = parseInt(numFull[1], 10);
          const dt = tryParseToken(tok, carryMonth);
          if (dt && isValid(dt)) {
            const key = utcEpochDayKeyNumber(dt);
            if (!seen.has(key)) { seen.add(key); dates.push(dt); }
          }
        }
      }
    }

    setRowFormatSkipInfo(
      isCrossProgramMode ? { unknownOfferingIdCount, missingOfferingIdCount } : null
    );

    // For non-RowFormat input we keep the existing behavior of sorting dates
    // so the summary and creation order are chronological. For RowFormat we
    // preserve the order entered by the user (which may include duplicates and
    // out-of-order dates).
    if (!rowFormat) {
      dates.sort(compareAsc);
    }
    // Store extras in form for downstream creation
    const extrasRecord: Record<
      string,
      | {
          locationId?: string;
          title?: string;
          titleSuffix?: string;
          url?: string;
          regUrl?: string;
          description?: string;
          descriptionPrefix?: string;
          eligibilityNotes?: string;
          offeringId?: string;
          minAgeInYearsOverride?: number | null;
          maxAgeInYearsOverride?: number | null;
          minAgeGradeKeyOverride?: string;
          maxAgeGradeKeyOverride?: string;
          endDate?: Date;
          availabilityStatus?: string;
          signupStatus?: string;
          regEvents?: Array<{ eventKey: string; when?: Date; wasTimeProvided: boolean; note: string }>;
          overrideNumDays?: number | null;
          priceOverride?: number;
          priceSingleDayOverride?: number;
          priceRegularTier2Override?: number | null;
          priceRegularTier3Override?: number | null;
        }
      | Array<{
          locationId?: string;
          title?: string;
          titleSuffix?: string;
          url?: string;
          regUrl?: string;
          description?: string;
          descriptionPrefix?: string;
          eligibilityNotes?: string;
          offeringId?: string;
          minAgeInYearsOverride?: number | null;
          maxAgeInYearsOverride?: number | null;
          minAgeGradeKeyOverride?: string;
          maxAgeGradeKeyOverride?: string;
          endDate?: Date;
          availabilityStatus?: string;
          signupStatus?: string;
          regEvents?: Array<{ eventKey: string; when?: Date; wasTimeProvided: boolean; note: string }>;
          overrideNumDays?: number | null;
          priceOverride?: number;
          priceSingleDayOverride?: number;
          priceRegularTier2Override?: number | null;
          priceRegularTier3Override?: number | null;
        }>
    > = {};
    for (const d of dates) {
      const k = utcEpochDayKey(d);
      if (extrasByKey[k]) extrasRecord[k] = extrasByKey[k];
    }
    form.setValue("dateListExtras" as any, extrasRecord, { shouldValidate: false });
    return dates;
  }

  const normalizeTitleForCompare = (raw: unknown): string => {
    const s = String(raw ?? "")
      .normalize("NFKC")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return s;
  };

  const titleSimilarity = (aRaw: string, bRaw: string): number => {
    const a = normalizeTitleForCompare(aRaw);
    const b = normalizeTitleForCompare(bRaw);
    if (!a || !b) return 0;
    if (a === b) return 1;
    if (a.length < 2 || b.length < 2) return 0;
    const aBigrams = new Map<string, number>();
    for (let i = 0; i < a.length - 1; i++) {
      const bg = a.slice(i, i + 2);
      aBigrams.set(bg, (aBigrams.get(bg) ?? 0) + 1);
    }
    const bBigrams = new Map<string, number>();
    for (let i = 0; i < b.length - 1; i++) {
      const bg = b.slice(i, i + 2);
      bBigrams.set(bg, (bBigrams.get(bg) ?? 0) + 1);
    }
    let overlap = 0;
    for (const [bg, countA] of aBigrams) {
      const countB = bBigrams.get(bg) ?? 0;
      overlap += Math.min(countA, countB);
    }
    const denom = (a.length - 1) + (b.length - 1);
    return denom > 0 ? (2 * overlap) / denom : 0;
  };

  const dateListSummary = useMemo(() => {
    if (dateListInput.length > BULK_DATE_LIST_MAX_INPUT_CHARS) {
      return `Paste exceeds ${BULK_DATE_LIST_MAX_INPUT_CHARS.toLocaleString()} characters — trim or split into smaller chunks to avoid freezing the browser.`;
    }
    const count = parsedDateList.length;
    if (count === 0) return "0 sessions entered";
    if (count === 1) return `1 session entered, starting on: ${format(parsedDateList[0], "MMMM d, yyyy")}`;
    const first = parsedDateList[0];
    const last = parsedDateList[parsedDateList.length - 1];
    const base = `${count} sessions entered, starting from ${format(first, "MMMM d, yyyy")} to ${format(last, "MMMM d, yyyy")}`;
    const skip = rowFormatSkipInfo;
    const skipBits: string[] = [];
    if (skip?.unknownOfferingIdCount) {
      skipBits.push(`Will skip ${skip.unknownOfferingIdCount} row${skip.unknownOfferingIdCount === 1 ? '' : 's'}: unknown offeringId`);
    }
    if (skip?.missingOfferingIdCount) {
      skipBits.push(`Will skip ${skip.missingOfferingIdCount} row${skip.missingOfferingIdCount === 1 ? '' : 's'}: missing offeringId`);
    }
    if (rowFormatSpecText) {
      const extra = skipBits.length > 0 ? `  ${skipBits.join('  ')}` : '';
      return `${base}  Custom row format provided. Looking for rows in this format: ${rowFormatSpecText}${extra}`;
    }
    return skipBits.length > 0 ? `${base}  ${skipBits.join('  ')}` : base;
  }, [dateListInput, parsedDateList, rowFormatSpecText, rowFormatSkipInfo]);

  // KB-1460: per-session preview. In list/org mode the create source is
  // `parsedDateList`; in range mode it's the generated `previewDates`.
  const effectivePreviewDates = dateTab === "list" ? parsedDateList : previewDates;
  const watchedExtras = form.watch("dateListExtras" as any) as BulkPreviewExtras;
  const watchedDisplayTitle = form.watch("displayTitle");
  const watchedVariationKey = form.watch("variationKey");
  const watchedAvailabilityStatus = form.watch("availabilityStatus");

  const previewRows = useMemo(
    () => enumerateBulkPreviewRows(effectivePreviewDates, watchedExtras),
    [effectivePreviewDates, watchedExtras]
  );

  const previewLabelContext = useMemo<BulkPreviewLabelContext>(() => {
    const programLabelByKey: Record<string, string> = {};
    const programPriceByKey: Record<string, number | null | undefined> = {};
    for (const p of programs) {
      if (!p?.key) continue;
      programLabelByKey[p.key] = p.displayTitle || p.key;
      programPriceByKey[p.key] = p.priceRegular ?? p.regularPrice ?? undefined;
    }
    const availabilityLabelById: Record<string, string> = {};
    for (const opt of availabilityStatusOptions) {
      if (opt?.id) availabilityLabelById[opt.id] = opt.label || opt.id;
    }
    return {
      baseLocationId: String(location?.id || ""),
      baseLocationName: String((location as any)?.name || location?.id || ""),
      topLevel: {
        displayTitle: watchedDisplayTitle,
        variationKey: watchedVariationKey,
        availabilityStatus: watchedAvailabilityStatus,
      },
      locationNameById,
      programLabelByKey,
      programPriceByKey,
      availabilityLabelById,
    };
  }, [
    programs,
    availabilityStatusOptions,
    location,
    locationNameById,
    watchedDisplayTitle,
    watchedVariationKey,
    watchedAvailabilityStatus,
  ]);

  const includedPreviewCount = previewRows.length - excludedRowKeys.size;

  // Prune stale exclusions when the underlying session set changes (e.g. the
  // operator edits the date-list paste) so the count stays accurate.
  useEffect(() => {
    setExcludedRowKeys((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(previewRows.map((r) => r.key));
      let changed = false;
      const next = new Set<string>();
      for (const k of prev) {
        if (live.has(k)) next.add(k);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [previewRows]);

  const toggleRowExcluded = useCallback((key: string) => {
    setExcludedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Reset exclusions whenever the modal closes so a fresh open starts clean.
  useEffect(() => {
    if (!isOpen) setExcludedRowKeys(new Set());
  }, [isOpen]);

  const includeAllPreviewRows = useCallback(() => setExcludedRowKeys(new Set()), []);
  const excludeAllPreviewRows = useCallback(
    () => setExcludedRowKeys(new Set(previewRows.map((r) => r.key))),
    [previewRows]
  );

  const handleDateListChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setDateListInput(val);
    setParsedDateList(parseDateListInput(val));
    // Input changed; previous check result is stale.
    setFormatCheckResult(null);
    setDuplicateCheckResult(null);
  };

  const handleCheckDuplicatesClick = () => {
    type RowForDup = {
      rowIndex: number;
      startDateKey: string;
      endDateKey: string;
      numDaysKey: string;
      offeringIdKey: string;
      locationIdKey: string;
      urlKey: string;
      titleNorm: string;
      titleRaw: string;
    };

    const currentVariationKey = String(form.getValues("variationKey") || "").trim();
    const currentDisplayTitle = String(form.getValues("displayTitle") || "").trim();
    const formNumDaysOverride = (() => {
      const n = form.getValues("numDaysOverride" as any);
      return typeof n === "number" && Number.isFinite(n) && n >= 1 ? n : undefined;
    })();
    const extrasNow = (form.getValues("dateListExtras" as any) as any) ?? {};
    const perDayRowIndex = new Map<string, number>();
    const rows: RowForDup[] = [];

    const formatTitle = (extras: any) => {
      const hasTitleProvided = Object.prototype.hasOwnProperty.call(extras || {}, "title");
      const base = hasTitleProvided ? (extras?.title ?? "") : currentDisplayTitle;
      let out = String(base ?? "").trim();
      if (typeof extras?.titleSuffix === "string" && extras.titleSuffix.trim().length > 0) {
        const suffix = extras.titleSuffix.trim();
        const needsSpace = out.length > 0 && !out.endsWith(" ") && !suffix.startsWith(" ");
        out = `${out}${needsSpace ? " " : ""}${suffix}`.trim();
      }
      return out;
    };

    const calcEndDate = (startDate: Date, extras: any, numDays: number | undefined) => {
      if (extras?.endDate instanceof Date && !isNaN(extras.endDate as any)) return extras.endDate as Date;
      if (typeof numDays === "number" && numDays >= 1) {
        const d = new Date(startDate);
        d.setDate(d.getDate() + numDays - 1);
        return d;
      }
      return undefined;
    };

    for (let idx = 0; idx < parsedDateList.length; idx++) {
      const startDate = parsedDateList[idx];
      const epochKey = utcEpochDayKey(startDate);
      const rawExtras = extrasNow?.[epochKey];
      let extrasForRow: any | undefined;
      if (Array.isArray(rawExtras)) {
        const currentIndex = perDayRowIndex.get(epochKey) ?? 0;
        extrasForRow = rawExtras[currentIndex] ?? rawExtras[rawExtras.length - 1] ?? undefined;
        perDayRowIndex.set(epochKey, currentIndex + 1);
      } else {
        extrasForRow = rawExtras;
      }

      const rowNumDays = (() => {
        const n = extrasForRow?.overrideNumDays;
        if (typeof n === "number" && Number.isFinite(n) && n >= 1) return Math.floor(n);
        return formNumDaysOverride;
      })();
      const endDate = calcEndDate(startDate, extrasForRow, rowNumDays);
      const offeringId = typeof extrasForRow?.offeringId === "string" && extrasForRow.offeringId.trim()
        ? extrasForRow.offeringId.trim()
        : currentVariationKey;
      const locationId = typeof extrasForRow?.locationId === "string" ? extrasForRow.locationId.trim() : "";
      const urlKey = typeof extrasForRow?.url === "string" ? extrasForRow.url.trim() : "";
      const titleRaw = formatTitle(extrasForRow);
      rows.push({
        rowIndex: idx + 1,
        startDateKey: utcEpochDayKey(startDate),
        endDateKey: endDate ? utcEpochDayKey(endDate) : "",
        numDaysKey: rowNumDays != null ? String(rowNumDays) : "",
        offeringIdKey: offeringId,
        locationIdKey: locationId,
        urlKey,
        titleNorm: normalizeTitleForCompare(titleRaw),
        titleRaw,
      });
    }

    const issues: Array<{
      severity: "error" | "warning";
      criterion: "1" | "2" | "3" | "4";
      message: string;
      lineNumbers: number[];
      sampleTitles?: string[];
    }> = [];
    // Slightly wider fuzzy net to catch near-miss titles like singular/plural variants.
    const fuzzyThreshold = 0.86;

    const byExactKey = (items: RowForDup[], keyFn: (r: RowForDup) => string) => {
      const grouped = new Map<string, RowForDup[]>();
      for (const r of items) {
        const k = keyFn(r);
        const arr = grouped.get(k) ?? [];
        arr.push(r);
        grouped.set(k, arr);
      }
      return grouped;
    };

    // Criterion #1: exact all fields, locationId provided
    const withLocation = rows.filter(r => r.locationIdKey.length > 0);
    const c1Groups = byExactKey(withLocation, (r) =>
      [r.startDateKey, r.endDateKey, r.numDaysKey, r.offeringIdKey, r.locationIdKey, r.urlKey, r.titleNorm].join("|")
    );
    for (const group of c1Groups.values()) {
      if (group.length < 2) continue;
      issues.push({
        severity: "error",
        criterion: "1",
        message: `Criterion #1: exact duplicate rows for start/end/numDays/offeringId/locationId/url/title.`,
        lineNumbers: group.map(g => g.rowIndex),
        sampleTitles: group.map(g => g.titleRaw).slice(0, 3),
      });
    }

    // Criterion #2: same first 6 fields (including locationId, url), very similar title
    const c2Blocks = byExactKey(withLocation, (r) =>
      [r.startDateKey, r.endDateKey, r.numDaysKey, r.offeringIdKey, r.locationIdKey, r.urlKey].join("|")
    );
    for (const block of c2Blocks.values()) {
      if (block.length < 2) continue;
      for (let i = 0; i < block.length; i++) {
        for (let j = i + 1; j < block.length; j++) {
          const a = block[i];
          const b = block[j];
          if (a.titleNorm === b.titleNorm) continue; // already caught by criterion #1
          const score = titleSimilarity(a.titleRaw, b.titleRaw);
          if (score >= fuzzyThreshold) {
            issues.push({
              severity: "warning",
              criterion: "2",
              message: `Criterion #2: likely duplicate rows for start/end/numDays/offeringId/locationId/url (very similar title, score ${score.toFixed(2)}).`,
              lineNumbers: [a.rowIndex, b.rowIndex],
              sampleTitles: [a.titleRaw, b.titleRaw],
            });
          }
        }
      }
    }

    // Criteria #3/#4: no locationId provided
    const noLocation = rows.filter(r => r.locationIdKey.length === 0);
    // #3 exact
    const c3Groups = byExactKey(noLocation, (r) =>
      [r.startDateKey, r.endDateKey, r.numDaysKey, r.offeringIdKey, r.urlKey, r.titleNorm].join("|")
    );
    for (const group of c3Groups.values()) {
      if (group.length < 2) continue;
      issues.push({
        severity: "error",
        criterion: "3",
        message: `Criterion #3: exact duplicate rows for start/end/numDays/offeringId/url/title with no locationId provided.`,
        lineNumbers: group.map(g => g.rowIndex),
        sampleTitles: group.map(g => g.titleRaw).slice(0, 3),
      });
    }
    // #4 fuzzy
    const c4Blocks = byExactKey(noLocation, (r) =>
      [r.startDateKey, r.endDateKey, r.numDaysKey, r.offeringIdKey, r.urlKey].join("|")
    );
    for (const block of c4Blocks.values()) {
      if (block.length < 2) continue;
      for (let i = 0; i < block.length; i++) {
        for (let j = i + 1; j < block.length; j++) {
          const a = block[i];
          const b = block[j];
          if (a.titleNorm === b.titleNorm) continue; // already caught by criterion #3
          const score = titleSimilarity(a.titleRaw, b.titleRaw);
          if (score >= fuzzyThreshold) {
            issues.push({
              severity: "warning",
              criterion: "4",
              message: `Criterion #4: likely duplicate rows for start/end/numDays/offeringId/url with no locationId (very similar title, score ${score.toFixed(2)}).`,
              lineNumbers: [a.rowIndex, b.rowIndex],
              sampleTitles: [a.titleRaw, b.titleRaw],
            });
          }
        }
      }
    }

    setDuplicateCheckResult({
      checkedAt: new Date(),
      ok: issues.length === 0,
      issues,
      summary: {
        parsedDateListCount: parsedDateList.length,
        exactDuplicateCount: issues.filter(i => i.severity === "error").length,
        fuzzyDuplicateCount: issues.filter(i => i.severity === "warning").length,
        fuzzyThreshold,
      },
    });
  };

  const handleCheckFormattingClick = async () => {
    type IssueItem = {
      severity: "error" | "warning";
      message: string;
      lineNumber?: number;
      startDate?: string;
      title?: string;
    };
    const issues: IssueItem[] = [];
    const formatRowDate = (d: Date) => {
      try {
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      } catch {
        return String(d);
      }
    };

    // Re-parse from current textarea so validation uses fresh data (fixes false errors when
    // modal opens with prefill and user clicks "Check formatting" without editing).
    const parsed = parseDateListInput(dateListInput);
    setParsedDateList(parsed);

    // Required fields (mode-dependent):
    // - location-mode: require top-level Program + Session title unless RowFormat provides <offeringId>/<title>
    // - org-mode: require RowFormat (we hide Program/Session title fields)
    const rowFormatSpec = String(rowFormatSpecText || "");
    const rowFormatHas = (token: string) =>
      rowFormatSpec.toLowerCase().includes(String(token).toLowerCase());

    if (isOrgMode) {
      if (!rowFormatSpecText) {
        issues.push({ severity: "error", message: "RowFormat is required in org mode." });
      } else {
        const required = ["<locationId>", "<offeringId>", "<title>", "<startDate>", "<endDate>", "<url>"];
        const missing = required.filter((t) => !rowFormatHas(t));
        if (missing.length > 0) {
          issues.push({
            severity: "error",
            message: `RowFormat is missing required placeholder(s): ${missing.join(", ")}.`,
          });
        }
      }
    } else {
      const variationKey = (form.getValues("variationKey") || "").trim();
      const displayTitle = (form.getValues("displayTitle") || "").trim();
      const variationKeyOk = variationKey.length > 0 || rowFormatHas("<offeringId>");
      const displayTitleOk = displayTitle.length > 0 || rowFormatHas("<title>");
      if (!variationKeyOk) issues.push({ severity: "error", message: "Program is required." });
      if (!displayTitleOk) issues.push({ severity: "error", message: "Session title is required." });
    }

    // Parsed dates summary (use freshly parsed result, not state)
    const parsedCount = parsed.length;
    if (parsedCount === 0) {
      issues.push({
        severity: "error",
        message:
          "No sessions recognized from date list input. Check your RowFormat line and make sure each row matches it.",
      });
    }

    // OfferingId skip info (RowFormat cross-program mode)
    if (rowFormatSkipInfo?.missingOfferingIdCount) {
      issues.push({
        severity: isOrgMode ? "error" : "warning",
        message: `Will skip ${rowFormatSkipInfo.missingOfferingIdCount} row(s): missing offeringId.`,
      });
    }
    if (rowFormatSkipInfo?.unknownOfferingIdCount) {
      issues.push({
        severity: "warning",
        message: `Will skip ${rowFormatSkipInfo.unknownOfferingIdCount} row(s): unknown offeringId.`,
      });
    }

    // Extras schema limits (most common silent blocker)
    const extrasNow = (form.getValues("dateListExtras" as any) as unknown) ?? undefined;
    const extrasSummary = debugSummarizeDateListExtras(extrasNow);
    if (extrasSummary.descriptionOver2000Count > 0) {
      issues.push({
        severity: "error",
        message: `At least ${extrasSummary.descriptionOver2000Count} row(s) have description text longer than 2000 characters.`,
      });
    }
    if (extrasSummary.eligibilityNotesOver2000Count > 0) {
      issues.push({
        severity: "error",
        message: `At least ${extrasSummary.eligibilityNotesOver2000Count} row(s) have eligibility notes longer than 2000 characters.`,
      });
    }
    if (extrasSummary.descriptionPrefixOver2000Count > 0) {
      issues.push({
        severity: "error",
        message: `At least ${extrasSummary.descriptionPrefixOver2000Count} row(s) have description prefix text longer than 2000 characters.`,
      });
    }
    if (extrasSummary.invalidEndDateCount > 0) {
      issues.push({
        severity: "error",
        message: `At least ${extrasSummary.invalidEndDateCount} row(s) have an invalid end date.`,
      });
    }

    // NEW: RowFormat <locationId> validation (admin safeguard)
    //
    // Rules:
    // - locationId must exist as a campLocations doc
    // - locationId must be either the current location OR share the same providedByOrg as the current location
    // - offeringId/variationKey used for the row must exist in the target location
    //
    // NOTE: This runs only on explicit "Check formatting" to avoid extra reads while typing.
    try {
      const baseLocationId = String(location?.id || "").trim();
      const baseProvidedByOrgId = (() => {
        const raw = (location as any)?.providedByOrg;
        if (!raw) return "";
        if (typeof raw === "string") return raw;
        if (typeof raw?.id === "string") return raw.id;
        if (typeof raw?.path === "string") return String(raw.path).split("/").pop() || "";
        return "";
      })();

      const currentVariationKey = String(form.getValues("variationKey") || "").trim();

      // Reconstruct per-row extras in the same order rows will be created (including duplicates)
      const perDayRowIndex = new Map<string, number>();
      const perRow: Array<{
        epochKey: string;
        rowIndex: number;
        extras?: any;
      }> = [];
      for (let idx = 0; idx < parsed.length; idx++) {
        const startDate = parsed[idx];
        const epochKey = utcEpochDayKey(startDate);
        const rawExtras = (extrasNow as any)?.[epochKey] as any;
        let extrasForRow: any | undefined;
        if (Array.isArray(rawExtras)) {
          const currentIndex = perDayRowIndex.get(epochKey) ?? 0;
          extrasForRow = rawExtras[currentIndex] ?? rawExtras[rawExtras.length - 1] ?? undefined;
          perDayRowIndex.set(epochKey, currentIndex + 1);
        } else {
          extrasForRow = rawExtras;
        }
        perRow.push({ epochKey, rowIndex: idx, extras: extrasForRow });
      }

      if (isOrgMode) {
        // In org-mode, we require per-row values since most form inputs are hidden.
        const missingLocationId = perRow.filter(r => !String(r.extras?.locationId || "").trim()).length;
        const missingTitle = perRow.filter(r => !String(r.extras?.title || "").trim()).length;
        const missingUrl = perRow.filter(r => !String(r.extras?.url || "").trim()).length;
        const missingEndDate = perRow.filter(r => !(r.extras?.endDate instanceof Date) || isNaN(r.extras.endDate as any)).length;

        if (missingLocationId > 0) issues.push({ severity: "error", message: `Missing locationId on ${missingLocationId} row(s).` });
        if (missingTitle > 0) issues.push({ severity: "error", message: `Missing title on ${missingTitle} row(s).` });
        if (missingUrl > 0) issues.push({ severity: "error", message: `Missing url on ${missingUrl} row(s).` });
        if (missingEndDate > 0) issues.push({ severity: "error", message: `Missing or invalid endDate on ${missingEndDate} row(s).` });
      }

      const uniqueTargetLocationIds = new Set<string>();
      for (const r of perRow) {
        const locId = typeof r.extras?.locationId === "string" ? r.extras.locationId.trim() : "";
        if (locId) uniqueTargetLocationIds.add(locId);
      }

      const locationProgramsById = new Map<string, Set<string>>();
      const locationProvidedByOrgById = new Map<string, string>();

      // Current location is always allowed; we can validate offeringId against `programs` without extra reads.
      locationProgramsById.set(baseLocationId, new Set((programs || []).map(p => p.key)));
      locationProvidedByOrgById.set(baseLocationId, baseProvidedByOrgId);

      const looksLikeDocId = (s: string) => s.length > 0 && !s.includes("/") && !s.includes("\\");

      // Collect invalid locationId errors so we can emit one issue per affected row (with line/startDate/title).
      const invalidLocationErrors: Array<{ targetLocId: string; message: string }> = [];

      for (const targetLocId of uniqueTargetLocationIds) {
        // Skip base location
        if (targetLocId === baseLocationId) continue;

        if (!looksLikeDocId(targetLocId)) {
          invalidLocationErrors.push({
            targetLocId,
            message: `Invalid locationId "${targetLocId}". It must be a campLocations document ID.`,
          });
          continue;
        }

        // If we can't prove the base org, we can't safely validate orbit.
        if (!baseProvidedByOrgId) {
          invalidLocationErrors.push({
            targetLocId,
            message:
              `Cannot validate locationId "${targetLocId}" against this location's organization (missing providedByOrg on the current location).`,
          });
          continue;
        }

        const ref = doc(db as any, "campLocations", targetLocId);
        const snap = await getDoc(ref as any);
        if (!snap || typeof (snap as any).exists !== "function" || !(snap as any).exists()) {
          invalidLocationErrors.push({
            targetLocId,
            message: `Unknown locationId "${targetLocId}". No campLocations document exists with that ID.`,
          });
          continue;
        }
        const data = (snap as any).data?.() || {};
        const rawProvidedByOrg = (data as any)?.providedByOrg;
        const providedByOrgId =
          typeof rawProvidedByOrg === "string"
            ? rawProvidedByOrg
            : (typeof rawProvidedByOrg?.id === "string"
              ? rawProvidedByOrg.id
              : (typeof rawProvidedByOrg?.path === "string"
                ? String(rawProvidedByOrg.path).split("/").pop() || ""
                : ""));

        locationProvidedByOrgById.set(targetLocId, providedByOrgId);

        if (!providedByOrgId || providedByOrgId !== baseProvidedByOrgId) {
          invalidLocationErrors.push({
            targetLocId,
            message:
              `locationId "${targetLocId}" is outside this organization. Only locations that share the same providedByOrg as the current location are allowed.`,
          });
          continue;
        }

        const variations = Array.isArray((data as any)?.variations) ? (data as any).variations : [];
        const keys = new Set<string>();
        for (const v of variations) {
          const k = typeof v?.key === "string" ? v.key.trim() : "";
          if (k) keys.add(k);
        }
        locationProgramsById.set(targetLocId, keys);
      }

      for (const r of perRow) {
        const locId = typeof r.extras?.locationId === "string" ? r.extras.locationId.trim() : "";
        const err = invalidLocationErrors.find((e) => e.targetLocId === locId);
        if (err) {
          issues.push({
            severity: "error",
            message: err.message,
            lineNumber: r.rowIndex + 1,
            startDate: formatRowDate(parsed[r.rowIndex]),
            title: typeof r.extras?.title === "string" ? r.extras.title.trim() : undefined,
          });
        }
      }

      // Validate per-row program selection within the target location context.
      // This ensures offeringId (if present) is valid for the destination location.
      for (const r of perRow) {
        const targetLocId = (typeof r.extras?.locationId === "string" ? r.extras.locationId.trim() : "") || baseLocationId;
        // If locationId was invalid/out-of-orbit, we already emitted an error; skip deeper checks.
        if (targetLocId !== baseLocationId && !locationProgramsById.has(targetLocId)) continue;

        const offered = typeof r.extras?.offeringId === "string" ? r.extras.offeringId.trim() : "";
        const effectiveVariationKey = offered || currentVariationKey;
        if (!effectiveVariationKey) continue; // required-field check already covers top-level variationKey

        const validKeys = locationProgramsById.get(targetLocId);
        if (validKeys && !validKeys.has(effectiveVariationKey)) {
          const msg = offered
            ? `Row ${r.rowIndex + 1}: offeringId "${effectiveVariationKey}" is not valid for target locationId "${targetLocId}".`
            : `Row ${r.rowIndex + 1}: selected Program "${effectiveVariationKey}" is not valid for target locationId "${targetLocId}". Provide a valid <offeringId> for that location.`;
          issues.push({
            severity: "error",
            message: msg,
            lineNumber: r.rowIndex + 1,
            startDate: formatRowDate(parsed[r.rowIndex]),
            title: typeof r.extras?.title === "string" ? r.extras.title.trim() : undefined,
          });
        }
      }
    } catch (err: any) {
      issues.push({
        severity: "warning",
        message: `Could not fully validate locationId/offeringId targets: ${err?.message || String(err)}`,
      });
    }

    const ok = issues.every(i => i.severity !== "error");
    setFormatCheckResult({
      checkedAt: new Date(),
      ok,
      issues,
      summary: {
        parsedDateListCount: parsedCount,
        rowFormatSpecText,
        rowFormatSkipInfo,
        dateListExtrasSummary: extrasSummary,
      }
    });
  };
  
  // State for controlling popover open/close
  const [startDatePopoverOpen, setStartDatePopoverOpen] = useState(false);
  const [endDatePopoverOpen, setEndDatePopoverOpen] = useState(false);
  
  // NEW: Show single-day overlaps option when program has regular >1 day AND an alt of 1 day
  const [showSingleDayWeekdayOption, setShowSingleDayWeekdayOption] = useState(false);
  useEffect(() => {
    try {
      if (!selectedProgramKey || enumsMap.size === 0) {
        setShowSingleDayWeekdayOption(false);
        return;
      }
      const selectedProgram = programs.find(p => p.key === selectedProgramKey);
      if (!selectedProgram) {
        setShowSingleDayWeekdayOption(false);
        return;
      }

      const regularIncKey = selectedProgram.regularOfferIncrementKey || "";
      let regularDays = selectedProgram.numDays || null;
      if (regularDays == null && regularIncKey) {
        const enumItem = enumsMap.get(regularIncKey);
        regularDays = enumItem?.value ?? null;
      }
      const regularGT1 = (regularDays ?? 0) > 1;

      const hasAlt1 = (selectedProgram.regularOfferIncrementAlternatives || []).some(alt => {
        const enumItem = enumsMap.get(alt.key);
        return enumItem?.value === 1 || alt.key === "oi_1day";
      });

      setShowSingleDayWeekdayOption(regularGT1 && hasAlt1);
    } catch (err) {
      console.error("[BulkAddSessionsModal] Error computing showSingleDayWeekdayOption:", err);
      setShowSingleDayWeekdayOption(false);
    }
  }, [selectedProgramKey, programs, enumsMap]);
  
  // Reset form when modal is opened/closed
  useEffect(() => {
    if (!isOpen) {
      processedAddAnother.current = false; // Reset flag when modal closes
      return;
    }

    if (isOrgMode) {
      // Org-mode: force RowFormat list entry, and ignore "Add another" restore behavior.
      processedAddAnother.current = false;
      setDateTab("list");
      const next = (initialDateListInput ?? "RowFormat: ").trim().length > 0 ? (initialDateListInput ?? "RowFormat: ") : "RowFormat: ";
      setDateListInput(next);
      setSavedFormData(null);
      setSavedLockStates(null);

      form.reset({
        variationKey: "",
        displayTitle: "",
        description: "",
        eligibilityNotes: "",
        url: "",
        regUrl: "",
        recurrencePattern: "monday",
        startDate: undefined,
        endDate: undefined,
        availabilityStatus: defaultAvailabilityStatus,
        signupStatus: initialSignupStatus,
        numDaysOverride: null,
        myOfferIncrementKey: null,
      });
      setLockStates({
        description: "unset",
        eligibilityNotes: "unset",
        url: "unset",
        regUrl: "unset",
      });
      // Parse after reset so dateListExtras is not overwritten. Ensures "Check formatting"
      // works without requiring a user edit first.
      setParsedDateList(parseDateListInput(next));
    } else {
      // Admin-only: Always prefill the "Enter date list" textarea with "RowFormat: " on open,
      // even when using "Add another like this" flow
      if (isAdmin) {
        setDateListInput("RowFormat: ");
      }

      // If we have saved data from "Add another" and haven't processed it yet
      if (savedFormData && !processedAddAnother.current) {
        bulkModalDevLog("[BulkAddSessionsModal] Restoring form data from 'Add another'", savedFormData);
        form.reset(savedFormData);
        if (savedLockStates) setLockStates(savedLockStates);
        setSavedFormData(null); // Clear the saved data after using it
        processedAddAnother.current = true; // Mark as processed for this opening
      } else if (!savedFormData && !processedAddAnother.current) {
        // This is a fresh modal opening, not an "Add another" flow
        bulkModalDevLog("[BulkAddSessionsModal] Fresh modal open, resetting to defaults");
        form.reset({
          variationKey: "",
          displayTitle: "",
          description: "",
          eligibilityNotes: "",
          url: "",
          regUrl: "",
          recurrencePattern: "monday",
          startDate: undefined,
          endDate: undefined,
          availabilityStatus: defaultAvailabilityStatus,
          signupStatus: initialSignupStatus,
          numDaysOverride: null,
          myOfferIncrementKey: null,
        });
        setLockStates({
          description: "unset",
          eligibilityNotes: "unset",
          url: "unset",
          regUrl: "unset",
        });
      }
    }
    
    // This will run every time the modal opens, which is what we want for preview
    const values = form.getValues();
    const { startDate, endDate, recurrencePattern } = values;
    if (startDate && endDate && recurrencePattern) {
      const dates = generateSessionDates(startDate, endDate, recurrencePattern);
      setPreviewDates(dates);
    } else {
      setPreviewDates([]);
    }

  }, [
    isOpen,
    savedFormData,
    form,
    defaultAvailabilityStatus,
    defaultSignupStatus,
    isOrgMode,
    initialDateListInput,
    isAdmin,
    savedLockStates,
    initialSignupStatusId,
    signupStatusOptions,
  ]);
  
  // Update preview dates only when range fields change (not when dateListExtras updates from list paste)
  useEffect(() => {
    if (previewStartDate && previewEndDate && previewRecurrencePattern) {
      const dates = generateSessionDates(previewStartDate, previewEndDate, previewRecurrencePattern);
      setPreviewDates(dates);
      bulkModalDevLog(`[BulkAddSessionsModal] Preview updated: ${dates.length} dates for pattern ${previewRecurrencePattern}`);
    } else {
      setPreviewDates([]);
    }
  }, [previewStartDate, previewEndDate, previewRecurrencePattern]);

  // Ensure end-date calendar opens to the same month as selected start date
  const handleEndDateOpenChange = (open: boolean) => {
    setEndDatePopoverOpen(open);
    if (open) {
      const sd = form.getValues("startDate") as Date | undefined;
      if (sd && isValid(sd)) {
        setEndMonth(sd);
      }
    }
  };
  
  // Custom close handler to ensure form is reset
  const handleClose = () => {
    form.reset(); // Make sure form is reset before closing
    setAddAnotherChecked(false); // Reset "Add another" checkbox
    setSavedFormData(null); // Clear any saved form data
    setSavedLockStates(null);
    
    // JAIL BREAK: Reset local state variables
    setSelectedProgramLocal("");
    setShowSessionLengthLocal(false);
    setSessionLengthOptionsLocal([]);
    setDefaultIncrementLocal("");
    setLockStates({
      description: 'unset',
      eligibilityNotes: 'unset',
      url: 'unset',
      regUrl: 'unset'
    });
    
    onClose();
  };
  
  const handleSubmit = async (values: BulkAddSessionsFormValues) => {
    debugLog("Create sessions: onValid (handleSubmit) entered", {
      dateTab,
      isAdmin,
      isSaving,
      parsedDateListCount: parsedDateList.length,
      rowFormatSpecText,
      rowFormatSkipInfo,
      // Key required fields (these are also validated by zod)
      variationKey: values.variationKey,
      displayTitleLength: values.displayTitle?.length ?? 0,
      hasStartDate: !!values.startDate,
      hasEndDate: !!values.endDate,
    });
    debugLog("Create sessions: full form snapshot (onValid)", {
      values,
    });
    
    try {
      setIsSaving(true);
      debugLog("Create sessions: setIsSaving(true)", { dateTab });

      // Conditional required fields:
      // - location-mode: If RowFormat provides <offeringId> and/or <title>, we allow the top-level Program/Session title to be blank.
      // - org-mode: Program/Session title fields are hidden; RowFormat is required and must include core placeholders.
      const rowFormatSpec = String(rowFormatSpecText || "");
      const rowFormatSpecLower = rowFormatSpec.toLowerCase();
      const rowFormatHas = (token: string) => rowFormatSpecLower.includes(String(token).toLowerCase());

      if (isOrgMode) {
        // Org mode: allow either explicit RowFormat OR camp-data wrapped-curly table input.
        // Enforce by validating the parsed per-row extras instead of relying on placeholder presence.
        const extrasNow = (form.getValues("dateListExtras" as any) as unknown) ?? undefined;
        const missingByField = { locationId: 0, offeringId: 0, title: 0, url: 0, endDate: 0 };
        // KB-1460: only validate rows the operator is actually creating — an
        // excluded row's missing fields must not block the rest.
        const rowsToValidate = enumerateBulkPreviewRows(
          parsedDateList,
          extrasNow as BulkPreviewExtras
        ).filter((r) => !excludedRowKeys.has(r.key));
        for (const r of rowsToValidate) {
          const extrasForRow = r.extras;
          if (!String(extrasForRow?.locationId || "").trim()) missingByField.locationId++;
          if (!String(extrasForRow?.offeringId || "").trim()) missingByField.offeringId++;
          if (!String(extrasForRow?.title || "").trim()) missingByField.title++;
          if (!String(extrasForRow?.url || "").trim()) missingByField.url++;
          if (!(extrasForRow?.endDate instanceof Date) || isNaN(extrasForRow.endDate as any)) missingByField.endDate++;
        }

        if (missingByField.locationId || missingByField.offeringId || missingByField.title || missingByField.url || missingByField.endDate) {
          const parts: string[] = [];
          if (missingByField.locationId) parts.push(`locationId (${missingByField.locationId})`);
          if (missingByField.offeringId) parts.push(`offeringId (${missingByField.offeringId})`);
          if (missingByField.title) parts.push(`title (${missingByField.title})`);
          if (missingByField.url) parts.push(`url (${missingByField.url})`);
          if (missingByField.endDate) parts.push(`endDate (${missingByField.endDate})`);
          form.setError("dateList" as any, {
            type: "manual",
            message: `Org mode requires per-row values for: ${parts.join(", ")}.`,
          });
          debugLog("Create sessions: abort — org mode missing per-row extras", { missingByField });
          setIsSaving(false);
          return;
        }
      } else {
        const rowFormatHasOfferingId = rowFormatHas("<offeringId>");
        const rowFormatHasTitle = rowFormatHas("<title>");
        const variationKeyOk = String(values.variationKey || "").trim().length > 0 || rowFormatHasOfferingId;
        const displayTitleOk = String(values.displayTitle || "").trim().length > 0 || rowFormatHasTitle;
        if (!variationKeyOk) {
          form.setError("variationKey", { type: "manual", message: "Program is required" });
          debugLog("Create sessions: abort — missing variationKey (and no <offeringId> in RowFormat)", { rowFormatSpecText });
          setIsSaving(false);
          return;
        }
        if (!displayTitleOk) {
          form.setError("displayTitle", { type: "manual", message: "Session title is required" });
          debugLog("Create sessions: abort — missing displayTitle (and no <title> in RowFormat)", { rowFormatSpecText });
          setIsSaving(false);
          return;
        }
      }
      
      // Choose dates based on tab selection
      let dates: Date[] = [];
      if (dateTab === "list") {
        dates = parsedDateList;
        debugLog("Create sessions: using list-mode dates", {
          parsedDateListCount: dates.length,
          // helpful context for why list parsing may yield 0
          rowFormatSpecText,
          rowFormatSkipInfo,
        });
        if (dates.length === 0) {
          form.setError("dateList" as any, { type: "manual", message: "Enter at least one valid date" });
          debugLog("Create sessions: abort (list mode) — 0 parsed dates; setError(dateList)", {
            dateListInputFirst200: dateListInput.slice(0, 200),
            rowFormatSpecText,
            rowFormatSkipInfo,
          });
          setIsSaving(false);
          return;
        }
      } else {
        // Range mode: ensure startDate and endDate are present before generating
        if (!values.startDate) {
          form.setError("startDate" as any, { type: "manual", message: "Start date is required" });
          debugLog("Create sessions: validation error — missing startDate; setError(startDate)", {});
        }
        if (!values.endDate) {
          form.setError("endDate" as any, { type: "manual", message: "End date is required" });
          debugLog("Create sessions: validation error — missing endDate; setError(endDate)", {});
        }
        if (!values.startDate || !values.endDate) {
          debugLog("Create sessions: abort (range mode) — missing startDate/endDate", {
            hasStartDate: !!values.startDate,
            hasEndDate: !!values.endDate,
          });
          setIsSaving(false);
          return;
        }
        dates = generateSessionDates(values.startDate, values.endDate, values.recurrencePattern);
        debugLog("Create sessions: generated range-mode dates", {
          count: dates.length,
          recurrencePattern: values.recurrencePattern,
          startDate: values.startDate,
          endDate: values.endDate,
        });
        if (dates.length === 0) {
          // eslint-disable-next-line no-console
          console.error("[BulkAddSessionsModal] Create sessions: abort (range mode) — 0 generated dates", {
            recurrencePattern: values.recurrencePattern,
            startDate: values.startDate,
            endDate: values.endDate,
          });
          form.setError("recurrencePattern", { 
            type: "manual", 
            message: "No valid dates found for this pattern and range" 
          });
          debugLog("Create sessions: setError(recurrencePattern) and abort", {});
          setIsSaving(false);
          return;
        }
      }
      
      // KB-1460: drop operator-excluded rows from the preview table before
      // enqueueing. Filtering both the dates and the per-row extras (preserving
      // each day's array-vs-single shape) keeps the downstream creation path
      // untouched — it just receives fewer sessions.
      const { dates: includedDates, extras: includedExtras } = applyBulkPreviewExclusions(
        dates,
        values.dateListExtras as BulkPreviewExtras,
        excludedRowKeys
      );
      if (includedDates.length === 0) {
        form.setError("dateList" as any, {
          type: "manual",
          message: "Include at least one session to create.",
        });
        debugLog("Create sessions: abort — all preview rows excluded", {
          totalDates: dates.length,
          excluded: excludedRowKeys.size,
        });
        setIsSaving(false);
        return;
      }
      const submitValues: BulkAddSessionsFormValues = excludedRowKeys.size
        ? ({ ...values, dateListExtras: includedExtras } as BulkAddSessionsFormValues)
        : values;

      // Call the onSubmit callback with the form values and generated dates
      debugLog("Create sessions: calling onSubmit()", {
        dateTab,
        dateCount: includedDates.length,
        excludedCount: excludedRowKeys.size,
        firstDate: includedDates[0],
        lastDate: includedDates[includedDates.length - 1],
        variationKey: values.variationKey,
        displayTitle: values.displayTitle,
        addAnotherChecked,
      });
      await onSubmit(submitValues, includedDates);
      debugLog("Create sessions: onSubmit() resolved successfully", {
        dateCount: includedDates.length,
        addAnotherChecked,
      });
      
      // Handle "Add another" functionality
      if (addAnotherChecked) {
        // Save the current form data for reuse
        const formDataToSave = { ...values };
        bulkModalDevLog(`[BulkAddSessionsModal] Saved all form data for 'Add another like this'`, formDataToSave);
        setSavedFormData(formDataToSave);
        setSavedLockStates(lockStates);
        
        // Close and immediately reopen modal to "add another"
        onClose();
        
        // Small delay to ensure modal closes before reopening
        setTimeout(() => {
          // The parent component should handle reopening the modal
          // We'll dispatch a custom event that the parent can listen for
          const event = new CustomEvent('bulkSessionsAddAnother', {
            detail: { formData: values }
          });
          window.dispatchEvent(event);
        }, 100);
      } else {
        // Normal close without reopening
        handleClose();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[BulkAddSessionsModal] Create sessions: onSubmit() threw error", error);
      debugLog("Create sessions: failed (caught error)", {
        error,
        dateTab,
        parsedDateListCount: parsedDateList.length,
      });
    } finally {
      setIsSaving(false);
      debugLog("Create sessions: finished (finally) — setIsSaving(false)", {});
    }
  };
  
  // Function to trigger form submission from the modal's save button
  const handleSaveClick = () => {
    debugLog("Create sessions: save button clicked", {
      dateTab,
      isOpen,
      isAdmin,
      isSaving,
      parsedDateListCount: parsedDateList.length,
      rowFormatSpecText,
      rowFormatSkipInfo,
    });
    // Capture invalid submissions explicitly — otherwise it can look like “nothing happens”.
    form.handleSubmit(
      handleSubmit,
      (errors) => {
        const extrasNow = (form.getValues("dateListExtras" as any) as unknown) ?? undefined;
        const extrasSummary = debugSummarizeDateListExtras(extrasNow);
        if (
          extrasSummary.descriptionOver2000Count > 0 ||
          extrasSummary.eligibilityNotesOver2000Count > 0 ||
          extrasSummary.descriptionPrefixOver2000Count > 0 ||
          extrasSummary.invalidEndDateCount > 0
        ) {
          debugWarn(
            "Create sessions: likely reason for onInvalid — dateListExtras contains over-limit strings or invalid dates",
            extrasSummary as any
          );
        }
        debugLog("Create sessions: onInvalid (validation failed) — handleSubmit not called", {
          dateTab,
          errors,
          // Helpful to see whether user relied on RowFormat instead of required top fields
          variationKey: form.getValues("variationKey"),
          displayTitle: form.getValues("displayTitle"),
          // Extra debug to identify per-row schema violations (e.g., description > 2000)
          dateListExtrasSummary: extrasSummary,
        });
      }
    )();
  };
  
  return (
    <BigFormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isOrgMode ? "Bulk add sessions for org" : "Bulk add sessions"}
      onSave={handleSaveClick}
      isSaving={isSaving}
      saveLabel="Create sessions"
      showAddAnother={!isOrgMode}
      addAnotherChecked={addAnotherChecked}
      onAddAnotherChange={setAddAnotherChecked}
    >
      <Form {...form}>
        <form
          ref={formRef}
          onSubmit={form.handleSubmit(handleSubmit, (errors) => {
            const extrasNow = (form.getValues("dateListExtras" as any) as unknown) ?? undefined;
            const extrasSummary = debugSummarizeDateListExtras(extrasNow);
            if (
              extrasSummary.descriptionOver2000Count > 0 ||
              extrasSummary.eligibilityNotesOver2000Count > 0 ||
              extrasSummary.descriptionPrefixOver2000Count > 0 ||
              extrasSummary.invalidEndDateCount > 0
            ) {
              debugWarn(
                "Create sessions: likely reason for onInvalid — dateListExtras contains over-limit strings or invalid dates",
                extrasSummary as any
              );
            }
            debugLog("Create sessions: onInvalid (form submit event) — validation failed", {
              dateTab,
              errors,
              dateListExtrasSummary: extrasSummary,
            });
          })}
          className="space-y-6"
        >
          {/* Two-column layout for top rows */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Row 1, Col 1: Program Selection */}
            {!isOrgMode && (
              <FormField
                control={form.control}
                name="variationKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Program <span className="text-red-500">*</span></FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value);
                        
                        if (value) {
                          const selectedVariation = programs.find(v => v.key === value);
                          if (selectedVariation) {
                            // For admin users, auto-populate session title with program name
                            if (isAdmin) {
                              form.setValue("displayTitle", selectedVariation.displayTitle || selectedVariation.key);
                            }
                            
                            // Respect lock states: only overwrite fields that are not locked
                            bulkModalDevLog('[BulkAddSessionsModal] Auto-populating fields from program (respecting locks)', {
                              program: selectedVariation.displayTitle,
                            });
                            applyProgramToSessionForm(
                              selectedVariation,
                              (field, value) => form.setValue(field, value),
                              shouldOverwriteFromProgram,
                            );
                          }
                        }
                      }} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a program" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {programs.length > 0 ? (
                          programs.map((variation) => (
                            <SelectItem key={variation.key} value={variation.key}>
                              {variation.displayTitle || variation.key}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem disabled value="no-programs">
                            No programs available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            {/* Row 1, Col 2: Registration Status */}
            <FormField
              control={form.control}
              name="signupStatus"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Registration status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select registration status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {signupStatusOptions.filter((o) => isSignupStatus(o.id)).map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Row 2, Col 1: Session Title */}
            {!isOrgMode && (
              <FormField
                control={form.control}
                name="displayTitle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Session title <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="Enter session title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            {/* Row 2, Col 2: Availability Status */}
            <FormField
              control={form.control}
              name="availabilityStatus"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Availability status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select availability status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availabilityStatusOptions.filter((o) => isAvailabilityStatus(o.id)).map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* JAIL BREAK: Session Length Selector - moved to appear below Session Title */}
            {!isOrgMode && showSessionLengthLocal && (
              <FormField
                control={form.control}
                name="numDaysOverride"
                render={({ field }) => {
                  // Find the current increment key based on numDaysOverride value
                  const currentNumDays = field.value;
                  let currentIncrementKey = "";

                  if (currentNumDays && enumsMap.size > 0) {
                    // Find the increment key that matches the current numDays
                    for (const option of sessionLengthOptionsLocal) {
                      const enumItem = enumsMap.get(option.value);
                      if (enumItem && enumItem.value === currentNumDays) {
                        currentIncrementKey = option.value;
                        break;
                      }
                    }
                  }

                  // If no match found, use the default increment key
                  if (!currentIncrementKey && defaultIncrementLocal) {
                    currentIncrementKey = defaultIncrementLocal;
                  }

                  return (
                    <FormItem>
                      <FormLabel>Session length <span className="text-red-500">*</span></FormLabel>
                      <Select
                        value={currentIncrementKey}
                        onValueChange={(value) => {
                          bulkModalDevLog("[BulkAddSessionsModal] JAIL BREAK - Session length select changed:", value);
                          handleSessionLengthChange(value);
                        }}
                        disabled={incrementOptionsLoading || sessionLengthOptionsLocal.length === 0}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select session length" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {sessionLengthOptionsLocal.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                              {option.isDefault && " (default)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}
          </div>
          
          {/* First horizontal separator */}
          <Separator className="my-4" />
          
          {/* Date selection now within tabs and bordered */}
          <div className="border rounded-md p-4">
            <Tabs
              value={isOrgMode ? "list" : dateTab}
              onValueChange={(v) => {
                if (isOrgMode) return;
                setDateTab(v as any);
              }}
              className="w-full"
            >
              {!isOrgMode && (
                <TabsList className="mb-3">
                  <TabsTrigger value="range">Enter date range</TabsTrigger>
                  <TabsTrigger value="list">Enter date list</TabsTrigger>
                </TabsList>
              )}
              {!isOrgMode && (
                <TabsContent value="range" className="mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="recurrencePattern"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>Create sessions on...</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-col space-y-1"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="weekdays" id="weekdays" />
                              <Label htmlFor="weekdays">Every weekday (Mon-Fri)</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="monday" id="monday" />
                              <Label htmlFor="monday">Every Monday</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="tuesday" id="tuesday" />
                              <Label htmlFor="tuesday">Every Tuesday</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="wednesday" id="wednesday" />
                              <Label htmlFor="wednesday">Every Wednesday</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="thursday" id="thursday" />
                              <Label htmlFor="thursday">Every Thursday</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="friday" id="friday" />
                              <Label htmlFor="friday">Every Friday</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="saturday" id="saturday" />
                              <Label htmlFor="saturday">Every Saturday</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="sunday" id="sunday" />
                              <Label htmlFor="sunday">Every Sunday</Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Start date</FormLabel>
                          <Popover
                            open={startDatePopoverOpen}
                            onOpenChange={setStartDatePopoverOpen}
                          >
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "MMM dd, yyyy")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <div className="flex justify-between items-center p-2">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setStartMonth(subMonths(startMonth, 1))}
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <div className="text-sm font-medium">
                                  {format(startMonth, "MMMM yyyy")}
                                </div>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setStartMonth(addMonths(startMonth, 1))}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                              <Separator />
                              <Calendar
                                month={startMonth}
                                onMonthChange={setStartMonth}
                                mode="single"
                                selected={field.value}
                                onSelect={(date) => {
                                  field.onChange(date);
                                  setStartDatePopoverOpen(false);
                                }}
                                initialFocus
                                showNav={false}
                                className="rounded-md border-0"
                                classNames={{
                                  caption_label: "hidden",
                                  day_today: "bg-accent text-accent-foreground",
                                  day: "h-10 w-10 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground",
                                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                                  head_cell: "text-muted-foreground rounded-md w-10 font-normal text-[0.8rem] h-10",
                                  caption: "relative flex justify-center items-center h-12",
                                  nav_button_previous: "absolute left-1",
                                  nav_button_next: "absolute right-1"
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>End date</FormLabel>
                          <Popover
                            open={endDatePopoverOpen}
                            onOpenChange={handleEndDateOpenChange}
                          >
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "MMM dd, yyyy")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <div className="flex justify-between items-center p-2">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setEndMonth(subMonths(endMonth, 1))}
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <div className="text-sm font-medium">
                                  {format(endMonth, "MMMM yyyy")}
                                </div>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => setEndMonth(addMonths(endMonth, 1))}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                              <Separator />
                              <Calendar
                                month={endMonth}
                                onMonthChange={setEndMonth}
                                mode="single"
                                selected={field.value}
                                onSelect={(date) => {
                                  field.onChange(date);
                                  setEndDatePopoverOpen(false);
                                }}
                                initialFocus
                                showNav={false}
                                className="rounded-md border-0"
                                classNames={{
                                  caption_label: "hidden",
                                  day_today: "bg-accent text-accent-foreground",
                                  day: "h-10 w-10 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground",
                                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                                  head_cell: "text-muted-foreground rounded-md w-10 font-normal text-[0.8rem] h-10",
                                  caption: "relative flex justify-center items-center h-12",
                                  nav_button_previous: "absolute left-1",
                                  nav_button_next: "absolute right-1"
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </TabsContent>
              )}
              <TabsContent value="list" className="mt-0">
                <FormField
                  control={form.control}
                  name={"dateList" as any}
                  render={() => (
                    <FormItem>
                      <FormLabel>
                        Enter dates on separate lines in month/day/year format ('2/21/25') or
                        <Dialog>
                          <DialogTrigger asChild>
                            <button type="button" className="ml-1 underline text-blue-600 hover:text-blue-700">see all format options</button>
                          </DialogTrigger>
                          <DialogContent className="max-w-[800px]">
                            <DialogHeader>
                              <DialogTitle>Date list format options</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 text-sm max-h-[500px] overflow-auto">
                              <p>You can enter dates in any of the following formats. Separate dates by new lines, commas, periods, or spaces. We deduplicate and sort them automatically.</p>
                                  <ul className="list-disc pl-5 space-y-1">
                                <li><strong>M/D/YY</strong> or <strong>M/D/YYYY</strong> — e.g., 2/21/25 or 2/21/2025.</li>
                                <li><strong>M/D</strong> (no year) — we assume the next occurrence after today. e.g., 2/1.</li>
                                <li><strong>Month D</strong> — full month name, e.g., February 1.</li>
                                <li><strong>Mon D</strong> — short month name, e.g., Feb 1 (with or without a trailing period: "Feb." 1).</li>
                                <li><strong>Month carryover</strong> — within the same line, you can omit the month after the first date. For example: "2/1,4,6,7,17" means February 1, 4, 6, 7, and 17.</li>
                                <li><strong>Separators</strong> — newlines, commas, periods, or spaces are all accepted between dates.</li>
                              </ul>
                              <p>Examples:</p>
                              <pre className="bg-muted p-2 rounded text-xs">{`2/1
2/1,4,6,7,17
3/15/25
4/10/2026
February 1
Feb 1
Feb. 1
7/1.2.3`}</pre>
                              {isAdmin && (
                                <div className="mt-4 border-t pt-3">
                                  <h4 className="font-semibold mb-1">Admin-only super advanced options</h4>
                                  <p className="mb-2">If the first non-empty line starts with <code>RowFormat:</code>, we will parse subsequent lines using that custom format. Use these placeholders:</p>
                              <ul className="list-disc pl-5 space-y-1">
                                    <li><code>&lt;locationId&gt;</code>: optional per-row target location ID (a <code>campLocations</code> document ID). If provided, that row is created as a session under that location instead of the current location. Only locations belonging to the same organization (same <code>providedByOrg</code>) are allowed.</li>
                                    <li><code>&lt;offeringId&gt;</code>: selects the program for that row (must match the program's "Internal ID"). If unknown or missing, the row is skipped. When <code>&lt;offeringId&gt;</code> is present in RowFormat, the Program dropdown at the top of this modal is ignored. However, rows whose <code>&lt;offeringId&gt;</code> matches the selected Program will still use the relevant UI field values for any fields not overridden in that row.</li>
                                    <li><code>&lt;startDate&gt;</code>: the start date for that row (supports all date formats above).</li>
                                    <li><code>&lt;endDate&gt;</code>: the end date for that row (supports all date formats above). If omitted, the end date will be computed from program/session length as usual.</li>
                                    <li><code>&lt;numDays&gt;</code>: optional per-row session length override. Use an integer (e.g., <code>5</code>) to force that many days, or the string <code>dateDiff</code> to compute length as <em>1 + (endDate − startDate)</em> in days. When <code>dateDiff</code> is used, <code>&lt;endDate&gt;</code> must also be provided for that row.</li>
                                    <li><code>&lt;title&gt;</code>: sets the session title for that row.</li>
                                    <li><code>&lt;title.suffix&gt;</code>: appended to the end of the session title for that row. For example, if your program title is "Summer Camp" and the suffix is "- Week 1", the created title becomes "Summer Camp - Week 1". If a row has both <code>&lt;title&gt;</code> and <code>&lt;title.suffix&gt;</code>, the final title will be <code>&lt;title&gt;+&lt;title.suffix&gt;</code>.</li>
                                    <li><code>&lt;url&gt;</code>: sets the session URL for that row.</li>
                                  <li><code>&lt;regUrl&gt;</code>: sets the registration URL for that row (overrides the "Registration" field below when present).</li>
                                    <li><code>&lt;description&gt;</code>: sets the session description for that row.</li>
                                    <li><code>&lt;description.prefix&gt;</code>: optional prefix that will be prepended to the final description for that row. If <code>&lt;description&gt;</code> is also provided, the prefix is applied to the <code>&lt;description&gt;</code> value; otherwise it is applied to the existing description from the Program / form.</li>
                                    <li><code>&lt;eligibilityNotes&gt;</code>: optional eligibility notes override for that row.</li>
                                    <li><code>&lt;priceNote&gt;</code>: optional per-row price footnote override. Use <code>&lt;progDefault&gt;</code> to keep the program default instead of overriding.</li>
                                    <li><code>&lt;timingNote&gt;</code>: optional per-row timing footnote override. Use <code>&lt;progDefault&gt;</code> to keep the program default instead of overriding.</li>
                                    <li><code>&lt;minAge&gt;</code>: optional per-row minimum age (years). Use a number (e.g., <code>7</code>). Use blank or <code>default</code> to not override. Only applied if the Program allows session age overrides.</li>
                                    <li><code>&lt;maxAge&gt;</code>: optional per-row maximum age (years). Use a number (e.g., <code>12</code>). Use blank or <code>default</code> to not override. Only applied if the Program allows session age overrides.</li>
                                    <li><code>&lt;minGradeKey&gt;</code>: optional per-row minimum grade key (e.g., <code>g_rk</code>, <code>g_1</code>). Use blank or <code>default</code> to not override. Only applied if the Program allows session age overrides.</li>
                                    <li><code>&lt;maxGradeKey&gt;</code>: optional per-row maximum grade key (e.g., <code>g_r1</code>, <code>g_5</code>). Use blank or <code>default</code> to not override. Only applied if the Program allows session age overrides.</li>
                                    <li><code>&lt;price&gt;</code>: optional per-row price override. Enter a numeric value (e.g., <code>299.99</code>) to set the <code>priceOverride</code> for the session created from that row. All proper price handling logic from <code>prepareOfferData</code> will still be performed (priceCalc priority: currentSalePrice &gt; priceOverride &gt; alternative &gt; variation.priceRegular).</li>
                                    <li><code>&lt;price.singleDay&gt;</code>: optional per-row price override applied only to the single-day sessions created when "Also create single-day sessions…" is checked. Enter a numeric value (e.g., <code>70</code>). If omitted, single-day sessions will not receive a per-row price override from the RowFormat.</li>
                                    <li><code>&lt;priceTier2&gt;</code>: optional per-row tier-2 price. Enter a numeric value (e.g., <code>130</code>) to set the session&apos;s <code>pricingTierPriceOverrides.tier2</code> on the offer document. If omitted, the program default is used.</li>
                                    <li><code>&lt;priceTier3&gt;</code>: optional per-row tier-3 price. Enter a numeric value (e.g., <code>150</code>) to set the session&apos;s <code>pricingTierPriceOverrides.tier3</code> on the offer document. If omitted, the program default is used.</li>
                                    <li><code>&lt;regStatus&gt;</code>: optional per-row Registration Status. Must be a valid ID from the "Registration status" dropdown (type <code>signUpStatus</code>), e.g., <code>su_open</code>, <code>su_notYet</code>, <code>su_openLimited</code>. If invalid, it is ignored.</li>
                                    <li><code>&lt;availStatus&gt;</code>: optional per-row Availability Status. Must be a valid ID from the "Availability status" dropdown (type <code>availStatus</code>), e.g., <code>av_space</code>, <code>av_unknown</code>, <code>av_waitlist</code>. If invalid, it is ignored.</li>
                                    <li><code>&lt;regEvent1&gt;</code>, <code>&lt;regEvent2&gt;</code>, <code>&lt;regEvent3&gt;</code>: optional per-row Registration Event keys. Must be valid IDs from registration events (type <code>registrationEvents</code>), e.g., <code>e_open</code>, <code>e_closes</code>, <code>e_appDue</code>. If invalid, the event is ignored.</li>
                                    <li><code>&lt;regEvent1.datetime&gt;</code>, <code>&lt;regEvent2.datetime&gt;</code>, <code>&lt;regEvent3.datetime&gt;</code>: optional per-row Registration Event dates/times. Supports formats like "October 9, 2025 at 12:00:00 AM UTC-4", "Oct 9, 2025 at 4:00:00 AM UTC-5", or "10/9/25 12:00:00 AM UTC+1". If no timezone is provided, assumes UTC-4. If no time is provided, uses 12:00:00 AM in the provided/assumed timezone.</li>
                                    <li><code>&lt;regEvent1.note&gt;</code>, <code>&lt;regEvent2.note&gt;</code>, <code>&lt;regEvent3.note&gt;</code>: optional per-row Registration Event notes. Free text that will be stored with the corresponding registration event.</li>
                                  </ul>
                                  <p className="mt-2"><strong>Special sentinel:</strong> The literal value <code>&lt;progDefault&gt;</code> means “do not override” for program-default fields (e.g. <code>&lt;url&gt;</code>, <code>&lt;regUrl&gt;</code>, <code>&lt;description&gt;</code>, <code>&lt;eligibilityNotes&gt;</code>, <code>&lt;priceNote&gt;</code>, <code>&lt;timingNote&gt;</code>). Blank cells still mean an explicit empty-string override.</p>
                                  <p className="mt-2"><strong>Example with price:</strong> Use <code>&lt;price&gt;</code> to set a per-session price override. The price value will be applied as <code>priceOverride</code> and follows the standard price calculation priority (currentSalePrice &gt; priceOverride &gt; alternative &gt; variation.priceRegular).</p>
                                  <pre className="bg-muted p-2 rounded text-xs">{`RowFormat: |<title>|<startDate>|<endDate>|<price>|
|Summer Camp Week 1|07/06/2026|07/10/2026|299.99|
|Summer Camp Week 2|07/13/2026|07/17/2026|349.99|
|Summer Camp Week 3|07/20/2026|07/24/2026|299.99|`}</pre>
                                  <p className="mt-2"><strong>Example with price and price.singleDay:</strong> Set different prices for the multi-day session and the generated single-day sessions.</p>
                                  <pre className="bg-muted p-2 rounded text-xs">{`RowFormat: |<title>|<startDate>|<endDate>|<url>|<price>|<price.singleDay>|
|Digital Art Camp|Mar 23, 2026|Mar 27, 2026|https://www.hisawyer.com/creating-my-art/schedules/activity-set/1631182?source=camps|350|70|`}</pre>
                                  <p className="mt-2">You can also use an asterisk <code>*</code> as a wildcard for any content between separators (it will be ignored by the parser). Example:</p>
                                  <pre className="bg-muted p-2 rounded text-xs">{`RowFormat: |<url>|*|<description>|*|<startDate>|
|http://www.blah.com| asdfkj asdf ljasdf |this is a description| |Feb 2|
|http://www.blah.com| asdfkj asdf ljasdf |this is a description| |3/2|
|http://www.blah2.com| asdfkj asdf ljasdf |this is a description| asldkjf |March 2, 2027|`}</pre>
                                  <p>Each of these rows will be recognized and parsed according to the specified fields.</p>
                                  <p className="mt-2"><strong>Priority:</strong> Values provided via RowFormat (<code>&lt;title&gt;</code>, <code>&lt;url&gt;</code>, <code>&lt;regUrl&gt;</code>, <code>&lt;description&gt;</code>) always override the corresponding one-off fields in this form for the matching rows. If a placeholder is present but left empty in a row, it still supersedes the one-off field (resulting in an empty value for that row).</p>
                                </div>
                              )}
                            </div>
                            <DialogFooter>
                              <DialogClose asChild>
                                <Button type="button" variant="default">Close</Button>
                              </DialogClose>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          value={dateListInput}
                          onChange={handleDateListChange}
                          placeholder={`Examples:
2/1
2/1,4,6,7,17
3/15/25
4/10/2026
7/1.2.3`}
                          className={cn("min-h-[140px]", isOrgMode ? "min-h-[280px]" : "")}
                        />
                      </FormControl>
                      <div className="text-sm text-muted-foreground mt-2">{dateListSummary}</div>
                      <div className="mt-3 flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCheckFormattingClick}
                        >
                          Check formatting
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCheckDuplicatesClick}
                        >
                          Check for duplicates
                        </Button>
                        {formatCheckResult && (
                          <div className="text-xs text-muted-foreground">
                            Checked {formatCheckResult.checkedAt.toLocaleTimeString()}
                          </div>
                        )}
                      </div>

                      {formatCheckResult && (
                        <div
                          className={cn(
                            "mt-3 rounded-md border p-3 text-sm",
                            formatCheckResult.ok
                              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                              : "border-amber-200 bg-amber-50 text-amber-900"
                          )}
                        >
                          <div className="font-medium">
                            {formatCheckResult.ok
                              ? "No known problems found."
                              : "Formatting issues found."}
                          </div>
                          <div className="mt-1 text-xs opacity-90">
                            Parsed sessions: {formatCheckResult.summary.parsedDateListCount}
                            {formatCheckResult.summary.rowFormatSpecText
                              ? `  •  RowFormat detected`
                              : ""}
                          </div>

                          {formatCheckResult.issues.length > 0 && (
                            <ul className="mt-2 list-disc pl-5 space-y-1">
                              {formatCheckResult.issues.slice(0, 10).map((issue, idx) => (
                                <li key={idx}>
                                  <span className="font-semibold">
                                    {issue.severity === "error" ? "Error" : "Warning"}
                                  </span>
                                  {" — "}
                                  {issue.message}
                                  {(issue.lineNumber != null || issue.startDate != null || issue.title != null) && (
                                    <span className="ml-1 text-muted-foreground">
                                      {" "}
                                      (Line {issue.lineNumber ?? "—"}
                                      {issue.startDate != null ? ` · ${issue.startDate}` : ""}
                                      {issue.title != null ? ` · ${issue.title}` : ""})
                                    </span>
                                  )}
                                </li>
                              ))}
                              {formatCheckResult.issues.length > 10 && (
                                <li>
                                  …and {formatCheckResult.issues.length - 10} more
                                </li>
                              )}
                            </ul>
                          )}

                          {!formatCheckResult.ok &&
                            formatCheckResult.summary.dateListExtrasSummary.samples.length > 0 && (
                              <div className="mt-3 text-xs">
                                <div className="font-semibold">Examples</div>
                                <ul className="mt-1 list-disc pl-5 space-y-1">
                                  {formatCheckResult.summary.dateListExtrasSummary.samples.map((s, idx) => (
                                    <li key={idx}>
                                      dateKey {s.dateKey} ({formatUtcDateKey(s.dateKey)}), row {s.rowIndex}
                                      {s.offeringId ? ` • offeringId ${s.offeringId}` : ""}
                                      {s.title ? ` • title ${s.title}` : ""}
                                      {s.field && s.field !== "endDate" && typeof s.length === "number" && typeof s.limit === "number" ? (
                                        <>
                                          {" — "}
                                          <button
                                            type="button"
                                            className="underline underline-offset-2"
                                            onClick={() => {
                                              if (!s.value) return;
                                              setFormatCheckDetail({
                                                field: s.field as any,
                                                dateKey: s.dateKey,
                                                rowIndex: s.rowIndex,
                                                length: s.length!,
                                                limit: s.limit!,
                                                title: s.title,
                                                offeringId: s.offeringId,
                                                value: s.value,
                                              });
                                            }}
                                          >
                                            {s.field} length {s.length}
                                          </button>
                                        </>
                                      ) : s.field === "endDate" ? (
                                        " — invalid endDate"
                                      ) : (
                                        ""
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                        </div>
                      )}

                      {duplicateCheckResult && (
                        <div
                          className={cn(
                            "mt-3 rounded-md border p-3 text-sm",
                            duplicateCheckResult.ok
                              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                              : "border-amber-200 bg-amber-50 text-amber-900"
                          )}
                        >
                          <div className="font-medium">
                            {duplicateCheckResult.ok
                              ? "No duplicate candidates found."
                              : "Duplicate candidates found."}
                          </div>
                          <div className="mt-1 text-xs opacity-90">
                            Parsed sessions: {duplicateCheckResult.summary.parsedDateListCount}
                            {`  •  Exact duplicates: ${duplicateCheckResult.summary.exactDuplicateCount}`}
                            {`  •  Similar-title duplicates: ${duplicateCheckResult.summary.fuzzyDuplicateCount}`}
                            {`  •  Similarity threshold: ${duplicateCheckResult.summary.fuzzyThreshold}`}
                          </div>
                          <div className="mt-1 text-xs opacity-90">
                            Error = exact duplicate match. Warning = fuzzy (similar-title) duplicate candidate.
                          </div>

                          {duplicateCheckResult.issues.length > 0 && (
                            <ul className="mt-2 list-disc pl-5 space-y-1">
                              {duplicateCheckResult.issues.slice(0, 12).map((issue, idx) => (
                                <li key={idx}>
                                  <span className="font-semibold">
                                    {issue.severity === "error" ? "Error" : "Warning"}
                                  </span>
                                  {" — "}
                                  {issue.message}
                                  <span className="ml-1 text-muted-foreground">
                                    {`(Lines ${issue.lineNumbers.join(", ")})`}
                                  </span>
                                  {issue.sampleTitles && issue.sampleTitles.length > 0 && (
                                    <span className="ml-1 text-muted-foreground">
                                      {` • Titles: ${issue.sampleTitles.join(" | ")}`}
                                    </span>
                                  )}
                                </li>
                              ))}
                              {duplicateCheckResult.issues.length > 12 && (
                                <li>…and {duplicateCheckResult.issues.length - 12} more</li>
                              )}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Overlay to view full long field values */}
                      <Dialog
                        open={!!formatCheckDetail}
                        onOpenChange={(open) => {
                          if (!open) setFormatCheckDetail(null);
                        }}
                      >
                        <DialogContent className="z-[60] max-w-[900px]">
                          <DialogHeader>
                            <DialogTitle>Row detail</DialogTitle>
                          </DialogHeader>
                          {formatCheckDetail && (
                            <div className="space-y-3">
                              <div className="text-sm text-muted-foreground">
                                <div>
                                  <span className="font-medium">Date</span>: {formatUtcDateKey(formatCheckDetail.dateKey)} (dateKey {formatCheckDetail.dateKey})
                                </div>
                                <div>
                                  <span className="font-medium">Row</span>: {formatCheckDetail.rowIndex}
                                </div>
                                {formatCheckDetail.offeringId && (
                                  <div>
                                    <span className="font-medium">Offering</span>: {formatCheckDetail.offeringId}
                                  </div>
                                )}
                                {formatCheckDetail.title && (
                                  <div>
                                    <span className="font-medium">Title</span>: {formatCheckDetail.title}
                                  </div>
                                )}
                                <div>
                                  <span className="font-medium">Field</span>: {formatCheckDetail.field} ({formatCheckDetail.length} / {formatCheckDetail.limit})
                                </div>
                              </div>
                              <Textarea
                                value={formatCheckDetail.value}
                                readOnly
                                className="min-h-[260px] font-mono text-xs"
                              />
                            </div>
                          )}
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button type="button" variant="default">Close</Button>
                            </DialogClose>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Preview section — per-session table with per-row include/exclude (KB-1460) */}
          {previewRows.length > 0 && (
            <div className="mt-6 border rounded-md p-4 bg-slate-50">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <h3 className="font-medium text-sm">
                  Preview: {includedPreviewCount} of {previewRows.length} session
                  {previewRows.length === 1 ? "" : "s"} will be created
                </h3>
                <div className="flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    className="text-primary hover:underline disabled:opacity-40"
                    onClick={includeAllPreviewRows}
                    disabled={excludedRowKeys.size === 0}
                  >
                    Include all
                  </button>
                  <button
                    type="button"
                    className="text-muted-foreground hover:underline disabled:opacity-40"
                    onClick={excludeAllPreviewRows}
                    disabled={includedPreviewCount === 0}
                  >
                    Exclude all
                  </button>
                </div>
              </div>
              {includedPreviewCount === 0 && (
                <p className="text-xs text-destructive mb-2">
                  All sessions are excluded — include at least one to create.
                </p>
              )}
              <div className="max-h-72 overflow-y-auto rounded border bg-background">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead>Availability</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.slice(0, 300).map((row) => {
                      const labels = resolveBulkPreviewRowLabels(row, previewLabelContext);
                      const excluded = excludedRowKeys.has(row.key);
                      const dateLabel = row.endDate
                        ? `${format(row.startDate, "MMM d")} – ${format(row.endDate, "MMM d, yyyy")}`
                        : format(row.startDate, "MMM d, yyyy");
                      return (
                        <TableRow
                          key={row.key}
                          className={excluded ? "opacity-50" : undefined}
                          data-excluded={excluded ? "true" : "false"}
                        >
                          <TableCell className="align-top">
                            <Checkbox
                              checked={!excluded}
                              onCheckedChange={() => toggleRowExcluded(row.key)}
                              aria-label={excluded ? "Include this session" : "Exclude this session"}
                            />
                          </TableCell>
                          <TableCell className="align-top text-xs font-medium">{labels.title}</TableCell>
                          <TableCell className="align-top text-xs text-muted-foreground">{labels.programLabel}</TableCell>
                          <TableCell className="align-top text-xs text-muted-foreground">{labels.locationLabel}</TableCell>
                          <TableCell className="align-top text-xs whitespace-nowrap">{dateLabel}</TableCell>
                          <TableCell className="align-top text-xs text-right whitespace-nowrap">{labels.priceLabel}</TableCell>
                          <TableCell className="align-top text-xs text-muted-foreground">{labels.availabilityLabel}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {previewRows.length > 300 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Showing the first 300 of {previewRows.length} sessions. Rows beyond 300 are included by
                  default — narrow the date list to review them individually.
                </p>
              )}
            </div>
          )}

          {!isOrgMode && (
            <>
              {/* Second horizontal separator */}
              <Separator className="my-4" />
              <Separator className="my-4" />
              
              {/* Row 4: Session Description (full width) */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Session description</FormLabel>
                    <FormControl>
                      <div className="relative">
                        {lockStates.description !== 'unset' && (
                          <LockIconButton
                            name={lockStates.description === 'locked' ? 'lock' : 'lock_open_right'}
                            title={lockStates.description === 'locked' ? 'Locked (click to unlock) – Changing the program won\'t reset this value' : 'Unlocked (click to lock) – Changing the program will reset this value'}
                            onClick={() => toggleFieldLock('description')}
                          />
                        )}
                        <Textarea 
                          placeholder="Enter session description" 
                          className="min-h-[100px]" 
                          maxLength={2000}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            setFieldLockedOnUserEdit('description');
                          }} 
                        />
                        <div className="text-xs text-muted-foreground text-right mt-1">
                          {descriptionLength}/2000
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                    {showSingleDayWeekdayOption && (
                      <div className="mt-3">
                        <FormLabel>Also create single-day sessions for:</FormLabel>
                        <FormField
                          control={form.control}
                          name="createSingleDaysForOverlaps"
                          render={({ field }) => (
                            <div className="flex items-center space-x-2 mt-1">
                              <Checkbox
                                id="single-day-overlaps"
                                checked={!!field.value}
                                onCheckedChange={(checked) => {
                                  field.onChange(checked === true);
                                  // Pre-fill the suffix field when first checking any single-day option
                                  if (checked === true && !form.getValues("singleDayTitleSuffix")) {
                                    form.setValue("singleDayTitleSuffix", " (Single Day)");
                                  }
                                }}
                              />
                              <Label htmlFor="single-day-overlaps" className="text-sm font-normal">
                                All overlapping weekdays
                              </Label>
                            </div>
                          )}
                        />
                        {/* Conditional input for single-day title suffix */}
                        {form.watch("createSingleDaysForOverlaps") && (
                          <div className="mt-3">
                            <FormField
                              control={form.control}
                              name="singleDayTitleSuffix"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Single-day session title suffix (optional):</FormLabel>
                                  <FormControl>
                                    <Input placeholder="e.g., - Day Camp" {...field} value={field.value || ""} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </FormItem>
                )}
              />
              
              {/* Eligibility notes field */}
              <FormField
                control={form.control}
                name="eligibilityNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Eligibility notes</FormLabel>
                    <FormControl>
                      <div className="relative">
                        {lockStates.eligibilityNotes !== 'unset' && (
                          <LockIconButton
                            name={lockStates.eligibilityNotes === 'locked' ? 'lock' : 'lock_open_right'}
                            title={lockStates.eligibilityNotes === 'locked' ? 'Locked (click to unlock) – Changing the program won\'t reset this value' : 'Unlocked (click to lock) – Changing the program will reset this value'}
                            onClick={() => toggleFieldLock('eligibilityNotes')}
                          />
                        )}
                        <Textarea
                          placeholder="Enter eligibility requirements, age restrictions, etc..."
                          className="min-h-[80px]"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e);
                            setFieldLockedOnUserEdit('eligibilityNotes');
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {/* Session website field */}
              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Session website</FormLabel>
                    <FormControl>
                      <div className="relative">
                        {lockStates.url !== 'unset' && (
                          <LockIconButton
                            name={lockStates.url === 'locked' ? 'lock' : 'lock_open_right'}
                            title={lockStates.url === 'locked' ? 'Locked (click to unlock) – Changing the program won\'t reset this value' : 'Unlocked (click to lock) – Changing the program will reset this value'}
                            onClick={() => toggleFieldLock('url')}
                          />
                        )}
                        <Input
                          type="url"
                          placeholder="https://example.com/register"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e);
                            setFieldLockedOnUserEdit('url');
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {/* Program website — read-only, copied from the program on save */}
              <ProgramWebsiteReadOnly
                programUrl={sessionLinkDefaults(programs.find((p) => p.key === selectedProgramKey)).programUrl}
              />

              {/* Registration field */}
              <FormField
                control={form.control}
                name="regUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registration</FormLabel>
                    <FormControl>
                      <div className="relative">
                        {lockStates.regUrl !== 'unset' && (
                          <LockIconButton
                            name={lockStates.regUrl === 'locked' ? 'lock' : 'lock_open_right'}
                            title={lockStates.regUrl === 'locked' ? 'Locked (click to unlock) – Changing the program won\'t reset this value' : 'Unlocked (click to lock) – Changing the program will reset this value'}
                            onClick={() => toggleFieldLock('regUrl')}
                          />
                        )}
                        <Input
                          type="url"
                          placeholder="https://example.com/register-here"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e);
                            setFieldLockedOnUserEdit('regUrl');
                          }}
                        />
                      </div>
                    </FormControl>
                    <p className="text-sm text-muted-foreground mt-1">
                      Where a parent signs up. With no link here, the session shows no Register button.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Registration dates (optional) */}
              <RegistrationEventsSection form={form as any} />
            </>
          )}
        </form>
      </Form>
    </BigFormModal>
  );
};
